import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "../context/I18nContext";
import Pagination from "../components/Pagination/Pagination";
import * as adminService from "../services/admin";
import { apiBaseUrl } from "../services/auth";
import * as classesService from "../services/classes";
import * as lessonsService from "../services/lessons";
import * as studentsService from "../services/students";
import { getAvatarUrl } from "../utils/avatar";
import { computeTeacherHours, formatHours } from "../utils/teacherHours";
import useTimelineStartScroll from "../utils/useTimelineStartScroll";
import {
  dedupeTeacherSessions,
  getTeacherSessionKey,
} from "../utils/teacherSessionDedupe";
import "../App.css";
import "./Admin.css";

const PAYMENT_STATUSES = ["pending", "completed", "failed", "refunded"];

function formatDate(value, locale = "ar-EG-u-nu-latn") {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function formatCurrency(amount, currency = "TND", locale = "ar-EG-u-nu-latn") {
  if (amount === undefined || amount === null) return "-";
  const number = Number(amount);
  if (Number.isNaN(number)) return `${amount} ${currency}`;
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
  }).format(number);
}

function formatShortDate(value, locale = "ar-EG-u-nu-latn") {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
  }).format(date);
}

function toAbsoluteUrl(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;
  if (raw.startsWith("/")) return `${apiBaseUrl}${raw}`;
  return raw;
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}


function seriesToPath(series, width, height, { padding = 6 } = {}) {
  if (!Array.isArray(series) || series.length < 2) return "";
  const min = Math.min(...series);
  const max = Math.max(...series);
  const safeRange = max - min || 1;
  const innerW = Math.max(1, width - padding * 2);
  const innerH = Math.max(1, height - padding * 2);
  const step = innerW / (series.length - 1);

  const points = series.map((value, index) => {
    const x = padding + step * index;
    const y = padding + innerH - ((value - min) / safeRange) * innerH;
    return [x, y];
  });

  return points
    .map(
      ([x, y], index) =>
        `${index === 0 ? "M" : "L"}${x.toFixed(2)},${y.toFixed(2)}`,
    )
    .join(" ");
}

function getInitials(nameOrEmail) {
  if (!nameOrEmail) return "?";
  const words = String(nameOrEmail).trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "?";
  const first = words[0]?.[0] || "";
  const last = words.length > 1 ? words[words.length - 1]?.[0] : "";
  return (first + last).toUpperCase() || "?";
}

function getMonthWindow(monthOffset = 0) {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthOffset, 1);
  const end = new Date(now.getFullYear(), now.getMonth() - monthOffset + 1, 1);
  return { start, end };
}

function sumLessonHoursByWeekOfMonth(lessons, monthOffset = 0) {
  const { start, end } = getMonthWindow(monthOffset);
  const buckets = [0, 0, 0, 0];
  (lessons || []).forEach((lesson) => {
    const when = new Date(lesson?.scheduled_at);
    if (Number.isNaN(when.getTime())) return;
    if (when < start || when >= end) return;
    if (lesson?.status === "cancelled") return;

    const weekIndex = clampNumber(Math.floor((when.getDate() - 1) / 7), 0, 3);
    const minutes = Number(lesson?.duration_minutes);
    const safeMinutes = Number.isFinite(minutes) && minutes > 0 ? minutes : 60;
    buckets[weekIndex] += safeMinutes / 60;
  });
  return buckets;
}

const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

// Lessons that count for attendance: already happened and not cancelled.
function isCountableLesson(lesson, now = Date.now()) {
  const when = new Date(lesson?.scheduled_at).getTime();
  return (
    Number.isFinite(when) && when <= now && lesson?.status !== "cancelled"
  );
}

// Start of each of the last `weeks` weeks (oldest first), ending now.
function lastWeeksStarts(weeks) {
  const now = Date.now();
  return Array.from({ length: weeks }, (_, i) => now - (weeks - i) * WEEK_MS);
}

// Number of dates falling in each of the last `weeks` weeks.
function weeklyCounts(dates, weeks = 12) {
  const starts = lastWeeksStarts(weeks);
  const counts = starts.map(() => 0);
  dates.forEach((value) => {
    const ts = new Date(value).getTime();
    if (!Number.isFinite(ts)) return;
    const index = starts.findIndex((start) => ts >= start && ts < start + WEEK_MS);
    if (index >= 0) counts[index] += 1;
  });
  return counts;
}

// Running total at the end of each of the last `weeks` weeks.
function weeklyTotals(dates, weeks = 12) {
  const ends = lastWeeksStarts(weeks).map((start) => start + WEEK_MS);
  const stamps = dates
    .map((value) => new Date(value).getTime())
    .filter(Number.isFinite);
  return ends.map((end) => stamps.filter((ts) => ts < end).length);
}

// Attendance rate (0-100) for each of the last `weeks` weeks.
function weeklyAttendance(lessons, weeks = 12) {
  const now = Date.now();
  return lastWeeksStarts(weeks).map((start) => {
    const scoped = (lessons || []).filter((lesson) => {
      const ts = new Date(lesson?.scheduled_at).getTime();
      return ts >= start && ts < start + WEEK_MS && isCountableLesson(lesson, now);
    });
    if (!scoped.length) return 0;
    const done = scoped.filter((l) => l.status === "completed").length;
    return (done / scoped.length) * 100;
  });
}

function getAttendanceRate(lessons, { start, end } = {}) {
  const now = new Date();
  const scoped = (lessons || []).filter((lesson) => {
    const when = new Date(lesson?.scheduled_at);
    if (Number.isNaN(when.getTime())) return false;
    if (start && when < start) return false;
    if (end && when >= end) return false;
    // Upcoming lessons can't count as missed; cancelled ones didn't happen.
    return isCountableLesson(lesson, now.getTime());
  });
  if (!scoped.length) return null;
  const completed = scoped.filter((l) => l.status === "completed").length;
  return (completed / scoped.length) * 100;
}

function formatPercent(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "-";
  }
  return `${Math.round(Number(value))}%`;
}

// [start, end) for the teacher-hours period selector.
function getHoursRange(period, customFrom, customTo) {
  const now = new Date();
  if (period === "today") {
    const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }
  if (period === "custom" && customFrom) {
    const [fy, fm, fd] = customFrom.split("-").map(Number);
    const start = new Date(fy, fm - 1, fd);
    const [ty, tm, td] = (customTo || customFrom).split("-").map(Number);
    const end = new Date(ty, tm - 1, td + 1);
    return end > start
      ? { start, end }
      : { start, end: new Date(start.getTime() + 86400000) };
  }
  return getMonthWindow(0);
}

function TeacherHoursPanel({ t, locale, teachers, lessons }) {
  const [period, setPeriod] = useState("month");
  const today = toDateInputValue(new Date());
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);

  const { start, end } = getHoursRange(period, customFrom, customTo);
  const startTs = start.getTime();
  const endTs = end.getTime();

  const hours = useMemo(
    () =>
      computeTeacherHours(lessons, {
        start: new Date(startTs),
        end: new Date(endTs),
      }),
    [lessons, startTs, endTs],
  );

  const rows = useMemo(() => {
    const list = (teachers || []).map((teacher) => {
      const entry = hours.get(teacher.id) || {
        sessions: 0,
        doneMinutes: 0,
        plannedMinutes: 0,
      };
      return {
        teacher,
        ...entry,
        total: entry.doneMinutes + entry.plannedMinutes,
      };
    });
    return list.sort((a, b) => b.total - a.total);
  }, [teachers, hours]);

  const sum = rows.reduce(
    (acc, row) => ({
      sessions: acc.sessions + row.sessions,
      done: acc.done + row.doneMinutes,
      planned: acc.planned + row.plannedMinutes,
    }),
    { sessions: 0, done: 0, planned: 0 },
  );

  const periods = [
    { value: "today", label: t("adminPage.teacherHours.today") },
    { value: "month", label: t("adminPage.teacherHours.month") },
    { value: "custom", label: t("adminPage.teacherHours.custom") },
  ];
  const h = t("adminPage.teacherHours.hoursShort");

  return (
    <article className="admin-panel teacher-hours">
      <div className="teacher-hours__head">
        <div>
          <h3>{t("adminPage.teacherHours.title")}</h3>
          <p className="text-muted">{t("adminPage.teacherHours.subtitle")}</p>
        </div>
        <div className="teacher-hours__controls">
          <div className="teacher-hours__periods" role="group">
            {periods.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`teacher-hours__period${
                  period === item.value ? " is-active" : ""
                }`}
                aria-pressed={period === item.value}
                onClick={() => setPeriod(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>
          {period === "custom" && (
            <div className="teacher-hours__dates">
              <label>
                {t("adminPage.teacherHours.from")}
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)}
                />
              </label>
              <label>
                {t("adminPage.teacherHours.to")}
                <input
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={(e) => setCustomTo(e.target.value)}
                />
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="teacher-hours__summary">
        <div>
          <span>{t("adminPage.teacherHours.sessions")}</span>
          <strong>{sum.sessions}</strong>
        </div>
        <div>
          <span>{t("adminPage.teacherHours.done")}</span>
          <strong>
            {formatHours(sum.done, locale)} {h}
          </strong>
        </div>
        <div>
          <span>{t("adminPage.teacherHours.planned")}</span>
          <strong>
            {formatHours(sum.planned, locale)} {h}
          </strong>
        </div>
      </div>

      {rows.length ? (
        <div
          className="admin-table"
          style={{ "--admin-table-cols": "2fr 1fr 1fr 1fr 1fr" }}
        >
          <div className="admin-table__head">
            <span>{t("adminPage.teacherHours.teacher")}</span>
            <span>{t("adminPage.teacherHours.sessions")}</span>
            <span>{t("adminPage.teacherHours.done")}</span>
            <span>{t("adminPage.teacherHours.planned")}</span>
            <span>{t("adminPage.teacherHours.total")}</span>
          </div>
          {rows.map((row) => {
            const name =
              row.teacher.full_name ||
              `${row.teacher.first_name || ""} ${row.teacher.last_name || ""}`.trim() ||
              row.teacher.email;
            return (
              <div className="admin-table__row" key={row.teacher.id}>
                <span className="teacher-hours__name">{name}</span>
                <span>{row.sessions}</span>
                <span>
                  {formatHours(row.doneMinutes, locale)} {h}
                </span>
                <span>
                  {formatHours(row.plannedMinutes, locale)} {h}
                </span>
                <span className="teacher-hours__total">
                  {formatHours(row.total, locale)} {h}
                </span>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-muted">{t("adminPage.teacherHours.empty")}</p>
      )}
    </article>
  );
}

function AdminOverview({
  t,
  locale,
  user,
  users,
  students,
  allStudents,
  allTeachers,
  listCounts = {},
  lessons,
  classGroups,
  packs,
  payments,
  stats,
  unreadCount,
  subjects,
  onNavigate,
}) {
  const navigate = useNavigate();
  const greetingAvatarUrl = getAvatarUrl(user);
  const greetingName =
    user?.first_name ||
    user?.full_name ||
    user?.email ||
    t("adminPage.headerTitle");

  const { start: thisMonthStart, end: thisMonthEnd } = getMonthWindow(0);

  // `students` is only the first page of the list; use the full list/stats.
  const totalStudents =
    stats?.active_students ?? allStudents?.length ?? students?.length ?? 0;
  const createdDates = useMemo(
    () => (allStudents || []).map((student) => student?.created_at),
    [allStudents],
  );
  const activeStudents =
    stats?.new_students_month ??
    createdDates.filter((value) => {
      const created = new Date(value);
      return created >= thisMonthStart && created < thisMonthEnd;
    }).length;

  // Completed payments this month, one amount per currency (never mixed).
  const revenueEntries = useMemo(() => {
    const byCurrency = stats?.monthly_revenue_by_currency;
    if (byCurrency && typeof byCurrency === "object") {
      const entries = Object.entries(byCurrency)
        .map(([currency, amount]) => [currency, Number(amount) || 0])
        .filter(([, amount]) => amount > 0);
      if (entries.length) return entries;
    }
    return [["TND", Number(stats?.monthly_revenue) || 0]];
  }, [stats?.monthly_revenue_by_currency, stats?.monthly_revenue]);

  const unreadSupport = stats?.unread_support ?? unreadCount;
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);

  const attendance7d = getAttendanceRate(lessons, {
    start: sevenDaysAgo,
  });

  const attendanceThisMonth = getAttendanceRate(lessons, {
    start: thisMonthStart,
    end: thisMonthEnd,
  });

  const lessonHoursThis = sumLessonHoursByWeekOfMonth(lessons, 0);
  const lessonHoursLast = sumLessonHoursByWeekOfMonth(lessons, 1);

  // Real lesson hours only (no placeholder curve when there is no activity).
  const activityThisMonth = lessonHoursThis;
  const activityLastMonth = lessonHoursLast;

  const activityChartData = useMemo(() => {
    const roundHours = (value) => {
      const num = Number(value) || 0;
      return Math.round(num * 10) / 10;
    };

    return [0, 1, 2, 3].map((index) => ({
      week: `${index + 1}`,
      thisMonth: roundHours(activityThisMonth[index] ?? 0),
      lastMonth: roundHours(activityLastMonth[index] ?? 0),
    }));
  }, [activityThisMonth, activityLastMonth]);

  // Every teacher (not just the first page of users), busiest first this month.
  const teacherHoursThisMonth = useMemo(
    () =>
      computeTeacherHours(lessons, {
        start: thisMonthStart,
        end: thisMonthEnd,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lessons, thisMonthStart.getTime(), thisMonthEnd.getTime()],
  );
  const teachers = useMemo(() => {
    const source =
      allTeachers && allTeachers.length
        ? allTeachers
        : (users || []).filter((u) => u.role === "teacher");
    const minutesOf = (teacher) => {
      const entry = teacherHoursThisMonth.get(teacher.id);
      return entry ? entry.doneMinutes + entry.plannedMinutes : 0;
    };
    return [...source].sort((a, b) => minutesOf(b) - minutesOf(a)).slice(0, 5);
  }, [allTeachers, users, teacherHoursThisMonth]);

  // Most recently added students, from the full list.
  const topStudents = useMemo(
    () =>
      [...(allStudents && allStudents.length ? allStudents : students || [])]
        .sort(
          (a, b) =>
            new Date(b?.created_at || 0).getTime() -
            new Date(a?.created_at || 0).getTime(),
        )
        .slice(0, 6),
    [allStudents, students],
  );

  const studentAttendance = useMemo(() => {
    const counts = new Map();
    const totals = new Map();
    const now = Date.now();
    (lessons || []).forEach((lesson) => {
      const studentId = lesson?.student?.id ?? lesson?.student_id;
      if (!studentId || !isCountableLesson(lesson, now)) return;
      totals.set(studentId, (totals.get(studentId) || 0) + 1);
      if (lesson.status === "completed") {
        counts.set(studentId, (counts.get(studentId) || 0) + 1);
      }
    });
    return { counts, totals };
  }, [lessons]);

  const cards = [
    {
      key: "newStudents",
      label: t("adminPage.dashboard.cards.newStudents"),
      value: activeStudents,
      hint: t("adminPage.dashboard.cards.newStudentsHint"),
      series: weeklyCounts(createdDates),
      tone: "tone-a",
      linkTab: "students",
    },
    {
      key: "totalStudents",
      label: t("adminPage.dashboard.cards.totalStudents"),
      value: totalStudents,
      hint: t("adminPage.dashboard.cards.totalStudentsHint"),
      series: weeklyTotals(createdDates),
      tone: "tone-b",
      linkTab: "students",
    },
    {
      key: "dailyAttend",
      label: t("adminPage.dashboard.cards.dailyAttend"),
      value: formatPercent(attendance7d),
      hint: t("adminPage.dashboard.cards.dailyAttendHint"),
      series: weeklyAttendance(lessons),
      tone: "tone-c",
      linkTab: "timetable",
    },
    {
      key: "avgAttend",
      label: t("adminPage.dashboard.cards.averageAttend"),
      value: formatPercent(attendanceThisMonth),
      hint: t("adminPage.dashboard.cards.averageAttendHint"),
      series: weeklyAttendance(lessons),
      tone: "tone-d",
      linkTab: "timetable",
    },
  ];

  // Average score per program from scored tests (student and placement).
  const testResults = Array.isArray(stats?.test_results)
    ? stats.test_results
    : [];

  return (
    <section className="admin-overview">
      <div className="admin-overview__welcome">
        <div className="admin-overview__welcome-left">
          <div className="admin-overview__avatar" aria-hidden="true">
            {greetingAvatarUrl ? (
              <img src={greetingAvatarUrl} alt="" loading="lazy" />
            ) : (
              getInitials(greetingName)
            )}
          </div>
          <div>
            <div className="admin-overview__welcome-label">
              {t("adminPage.dashboard.welcome")}
            </div>
            <div className="admin-overview__welcome-title">{greetingName}</div>
          </div>
        </div>
        <div className="admin-overview__welcome-right">
          <button
            type="button"
            className="admin-overview__mini admin-overview__mini--link"
            onClick={() => onNavigate?.("payments")}
          >
            <div className="admin-overview__mini-label">
              {t("adminPage.stats.monthlyRevenue")}
            </div>
            <div className="admin-overview__mini-value">
              {revenueEntries
                .map(([currency, amount]) =>
                  formatCurrency(amount, currency, locale),
                )
                .join(" · ")}
            </div>
          </button>
          <button
            type="button"
            className="admin-overview__mini admin-overview__mini--link"
            onClick={() => onNavigate?.("support")}
          >
            <div className="admin-overview__mini-label">
              {t("adminPage.stats.supportMessages")}
            </div>
            <div className="admin-overview__mini-value">
              {unreadSupport} {t("adminPage.stats.unread")}
            </div>
          </button>
          <button
            type="button"
            className="admin-overview__mini admin-overview__mini--link"
            onClick={() => onNavigate?.("teachers")}
          >
            <div className="admin-overview__mini-label">
              {t("adminPage.stats.teachers")}
            </div>
            <div className="admin-overview__mini-value">
              <span className="admin-overview__mini-valueMain">
                {stats?.teachers_total ?? 0}
              </span>
              <span className="admin-overview__mini-valueSub">
                {stats?.pending_teachers ?? 0}{" "}
                {t("adminPage.teachers.status.pending")}
              </span>
            </div>
          </button>
        </div>
      </div>

      <div className="admin-overview__stats">
        {cards.map((card) => {
          const sparkW = 120;
          const sparkH = 36;
          const path = seriesToPath(card.series, sparkW, sparkH, {
            padding: 4,
          });
          return (
            <article key={card.key} className={`admin-stat-card ${card.tone}`}>
              <div>
                <div className="admin-stat-card__label">{card.label}</div>
                <div className="admin-stat-card__value">{card.value}</div>
                <div className="admin-stat-card__hint">{card.hint}</div>
              </div>
              {card.linkTab ? (
                <button
                  type="button"
                  className="admin-stat-card__link"
                  onClick={() => onNavigate?.(card.linkTab)}
                >
                  {t("adminPage.dashboard.viewAll")}
                </button>
              ) : null}
              <svg
                className="admin-stat-card__spark"
                viewBox={`0 0 ${sparkW} ${sparkH}`}
                role="img"
                aria-label={card.label}
              >
                <path
                  d={path}
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                />
              </svg>
            </article>
          );
        })}
      </div>

      <div className="admin-overview__grid">
        <article className="admin-panel admin-panel--activity">
          <div className="admin-panel__header">
            <div>
              <h2 className="admin-panel__title">
                {t("adminPage.dashboard.activityTitle")}
              </h2>
            </div>
            <div className="admin-panel__actions">
              <div className="admin-panel__legend">
                <span className="legend-item legend-item--a">
                  <span className="legend-dot" aria-hidden="true"></span>
                  {t("adminPage.dashboard.thisMonth")}
                </span>
                <span className="legend-item legend-item--b">
                  <span className="legend-dot" aria-hidden="true"></span>
                  {t("adminPage.dashboard.lastMonths")}
                </span>
              </div>
              <button
                type="button"
                className="admin-panel__link"
                onClick={() => onNavigate?.("timetable")}
              >
                {t("adminPage.dashboard.viewAll")}
              </button>
            </div>
          </div>

          <div
            className="admin-activity-chart"
            role="img"
            aria-label={t("adminPage.dashboard.activityTitle")}
          >
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={activityChartData}
                margin={{ top: 10, right: 12, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  stroke="rgba(15, 35, 78, 0.08)"
                  vertical={false}
                />
                <XAxis
                  dataKey="week"
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "rgba(15, 35, 78, 0.6)", fontSize: 12 }}
                />
                <YAxis
                  tickLine={false}
                  axisLine={false}
                  tick={{ fill: "rgba(15, 35, 78, 0.6)", fontSize: 12 }}
                  width={44}
                  domain={[0, "auto"]}
                  tickFormatter={(value) => `${value}h`}
                />
                <Tooltip
                  formatter={(value) => [`${value}h`, ""]}
                  labelFormatter={(label) => `Week ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="thisMonth"
                  stroke="rgba(30, 75, 186, 0.95)"
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                />
                <Line
                  type="monotone"
                  dataKey="lastMonth"
                  stroke="rgba(247, 171, 22, 0.9)"
                  strokeWidth={3}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </article>
        <article className="admin-panel admin-panel--manage">
          <div className="admin-panel__header">
            <h2 className="admin-panel__title">
              {t("adminPage.dashboard.manageTitle")}
            </h2>
          </div>
          <div
            className="admin-activity-chart"
            style={{ paddingTop: "0.85rem" }}
          >
            <div
              className="admin-table"
              style={{ "--admin-table-cols": "2fr 1fr auto" }}
            >
              <div className="admin-table__head">
                <span>{t("adminPage.dashboard.manageColumns.section")}</span>
                <span>{t("adminPage.dashboard.manageColumns.count")}</span>
                <span>{t("adminPage.dashboard.manageColumns.open")}</span>
              </div>
              <div className="admin-table__row">
                <span>{t("adminPage.tabs.users")}</span>
                <span>{listCounts.users ?? users?.length ?? 0}</span>
                <span>
                  <button
                    type="button"
                    className="admin-inline-link"
                    onClick={() => onNavigate?.("users")}
                  >
                    {t("adminPage.dashboard.viewAll")}
                  </button>
                </span>
              </div>
              <div className="admin-table__row">
                <span>{t("adminPage.tabs.students")}</span>
                <span>{listCounts.students ?? students?.length ?? 0}</span>
                <span>
                  <button
                    type="button"
                    className="admin-inline-link"
                    onClick={() => onNavigate?.("students")}
                  >
                    {t("adminPage.dashboard.viewAll")}
                  </button>
                </span>
              </div>
              <div className="admin-table__row">
                <span>{t("adminPage.tabs.timetable")}</span>
                <span>
                  {listCounts.teachers ??
                    (users || []).filter((u) => u.role === "teacher").length}
                </span>
                <span>
                  <button
                    type="button"
                    className="admin-inline-link"
                    onClick={() => onNavigate?.("timetable")}
                  >
                    {t("adminPage.dashboard.viewAll")}
                  </button>
                </span>
              </div>
              <div className="admin-table__row">
                <span>{t("adminPage.tabs.classes")}</span>
                <span>{listCounts.classes ?? classGroups?.length ?? 0}</span>
                <span>
                  <button
                    type="button"
                    className="admin-inline-link"
                    onClick={() => onNavigate?.("classes")}
                  >
                    {t("adminPage.dashboard.viewAll")}
                  </button>
                </span>
              </div>
              <div className="admin-table__row">
                <span>{t("adminPage.tabs.packs")}</span>
                <span>{listCounts.packs ?? packs?.length ?? 0}</span>
                <span>
                  <button
                    type="button"
                    className="admin-inline-link"
                    onClick={() => onNavigate?.("packs")}
                  >
                    {t("adminPage.dashboard.viewAll")}
                  </button>
                </span>
              </div>
              <div className="admin-table__row">
                <span>{t("adminPage.tabs.payments")}</span>
                <span>{listCounts.payments ?? payments?.length ?? 0}</span>
                <span>
                  <button
                    type="button"
                    className="admin-inline-link"
                    onClick={() => onNavigate?.("payments")}
                  >
                    {t("adminPage.dashboard.viewAll")}
                  </button>
                </span>
              </div>
            </div>
          </div>
        </article>
        <article className="admin-panel admin-panel--exams">
          <div className="admin-panel__header">
            <h2 className="admin-panel__title">
              {t("adminPage.dashboard.examsTitle")}
            </h2>
            <button
              type="button"
              className="admin-panel__link"
              onClick={() => navigate("/tests")}
            >
              {t("adminPage.dashboard.viewAll")}
            </button>
          </div>
          <div className="admin-exams">
            {testResults.length ? (
              testResults.slice(0, 4).map((result) => {
                const percent = clampNumber(
                  Math.round(Number(result.average_percent) || 0),
                  0,
                  100,
                );
                return (
                  <div className="admin-exams__row" key={result.program_id}>
                    <div className="admin-exams__meta">
                      <div className="admin-exams__name">
                        {result.program}
                        <span className="admin-exams__count text-muted">
                          {" "}
                          · {result.submissions}{" "}
                          {t("adminPage.dashboard.examsSubmissions")}
                        </span>
                      </div>
                      <div className="admin-exams__value">{percent}%</div>
                    </div>
                    <div
                      className="admin-exams__bar"
                      role="progressbar"
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-valuenow={percent}
                    >
                      <span style={{ width: `${percent}%` }} />
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="admin-teachers__empty">
                {t("adminPage.dashboard.examsEmpty")}
              </div>
            )}
          </div>
        </article>

        <article className="admin-panel admin-panel--students">
          <div className="admin-panel__header">
            <h2 className="admin-panel__title">
              {t("adminPage.dashboard.studentsTitle")}
            </h2>
            <button
              type="button"
              className="admin-panel__link"
              onClick={() => onNavigate?.("students")}
            >
              {t("adminPage.dashboard.viewAll")}
            </button>
          </div>
          <div
            className="admin-table"
            style={{ "--admin-table-cols": "110px 2fr 2fr 140px" }}
          >
            <div className="admin-table__head">
              <span>{t("adminPage.dashboard.studentsColumns.id")}</span>
              <span>{t("adminPage.dashboard.studentsColumns.student")}</span>
              <span>{t("adminPage.dashboard.studentsColumns.parent")}</span>
              <span>{t("adminPage.dashboard.studentsColumns.attends")}</span>
            </div>
            {topStudents.map((s) => {
              const studentId = s.id;
              const total = studentAttendance.totals.get(studentId) || 0;
              const done = studentAttendance.counts.get(studentId) || 0;
              const pct = total ? Math.round((done / total) * 100) : 0;
              return (
                <div className="admin-table__row" key={s.id}>
                  <span>#{s.id}</span>
                  <span>{s.full_name}</span>
                  <span className="text-muted">{s.parent_email || "-"}</span>
                  <span>{total ? `${pct}%` : "-"}</span>
                </div>
              );
            })}
            {!topStudents.length && (
              <div className="admin-table__empty">
                {t("adminPage.dashboard.empty")}
              </div>
            )}
          </div>
        </article>

        <article className="admin-panel admin-panel--teachers">
          <div className="admin-panel__header">
            <h2 className="admin-panel__title">
              {t("adminPage.dashboard.teachersTitle")}
            </h2>
            <button
              type="button"
              className="admin-panel__link"
              onClick={() => onNavigate?.("teachers")}
            >
              {t("adminPage.dashboard.viewAll")}
            </button>
          </div>
          <div className="admin-teachers">
            {teachers.map((teacher) => {
              const name =
                teacher.first_name ||
                teacher.full_name ||
                teacher.email ||
                "Teacher";
              return (
                <div className="admin-teachers__row" key={teacher.id}>
                  <div className="admin-teachers__avatar" aria-hidden="true">
                    {getAvatarUrl(teacher) ? (
                      <img src={getAvatarUrl(teacher)} alt="" loading="lazy" />
                    ) : (
                      getInitials(name)
                    )}
                  </div>
                  <div className="admin-teachers__meta">
                    <div className="admin-teachers__name">{name}</div>
                    <div className="admin-teachers__sub text-muted">
                      {teacher.is_verified
                        ? t("adminPage.dashboard.teacherVerified")
                        : t("adminPage.dashboard.teacherPending")}
                      {" · "}
                      {formatHours(
                        (teacherHoursThisMonth.get(teacher.id)?.doneMinutes || 0) +
                          (teacherHoursThisMonth.get(teacher.id)?.plannedMinutes || 0),
                        locale,
                      )}{" "}
                      {t("adminPage.teacherHours.hoursShort")}{" "}
                      {t("adminPage.dashboard.thisMonthShort")}
                    </div>
                  </div>
                </div>
              );
            })}
            {!teachers.length && (
              <div className="admin-teachers__empty">
                {t("adminPage.dashboard.empty")}
              </div>
            )}
          </div>
        </article>
      </div>
    </section>
  );
}

function toDateInputValue(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function toLocalDateTimeInputValue(date) {
  const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

function parseDateInput(value) {
  if (!value) return null;
  const parts = String(value)
    .split("-")
    .map((p) => Number(p));
  if (parts.length !== 3) return null;
  const [year, month, day] = parts;
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function TimetableTab({
  t,
  dir,
  locale,
  users,
  lessons,
  classGroups,
  students,
  packs,
  accessToken,
  fixedTeacherId,
  onBack,
  onLessonsChange,
  onError,
}) {
  const teachersBodyRef = useRef(null);
  const canvasRef = useRef(null);
  const syncingScrollRef = useRef(false);

  const [selectedDate, setSelectedDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [timetableView, setTimetableView] = useState("day");
  const [timetableScope, setTimetableScope] = useState("teacher");
  const [scopeTeacherId, setScopeTeacherId] = useState("");
  const [scopeClassId, setScopeClassId] = useState("");
  const [scopeStudentId, setScopeStudentId] = useState("");
  const [teacherSearch, setTeacherSearch] = useState("");

  const [actionOpen, setActionOpen] = useState(false);
  const [actionLesson, setActionLesson] = useState(null);
  const [actionOccursAt, setActionOccursAt] = useState(null);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorSaving, setEditorSaving] = useState(false);
  const [editorLessonId, setEditorLessonId] = useState(null);
  const [editorLessonIds, setEditorLessonIds] = useState([]);
  const [editorLessonStudentIds, setEditorLessonStudentIds] = useState({});
  const [editorTeacherId, setEditorTeacherId] = useState(null);
  const [editorAssignMode, setEditorAssignMode] = useState("student");
  const [editorStudentId, setEditorStudentId] = useState("");
  const [editorClassId, setEditorClassId] = useState("");
  const [editorTitle, setEditorTitle] = useState("");
  const [editorScheduledAt, setEditorScheduledAt] = useState("");
  const [editorDuration, setEditorDuration] = useState(60);
  const [editorStatus, setEditorStatus] = useState("scheduled");
  const [editorIsRecurringWeekly, setEditorIsRecurringWeekly] = useState(false);

  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyLessonIds, setNotifyLessonIds] = useState([]);
  const [notifyStatus, setNotifyStatus] = useState("updated");
  const [notifyMessage, setNotifyMessage] = useState("");
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifySuccess, setNotifySuccess] = useState(false);
  const [notifySuccessCount, setNotifySuccessCount] = useState(null);

  const resolveSessionLessons = useCallback(
    (lesson) => {
      if (!lesson) return [];
      const key = getTeacherSessionKey(lesson);
      if (!key) return lesson?.id ? [lesson] : [];

      const all = Array.isArray(lessons) ? lessons : [];
      const matches = all.filter((l) => getTeacherSessionKey(l) === key);
      if (matches.length) return matches;
      return [lesson];
    },
    [lessons],
  );

  const teacherOptions = useMemo(() => {
    const list = (users || []).filter((u) => u.role === "teacher");
    return [...list].sort((a, b) =>
      String(a.full_name || a.email || "").localeCompare(
        String(b.full_name || b.email || ""),
        locale,
        { sensitivity: "base" },
      ),
    );
  }, [users, locale]);

  const normalizeToLocalDayStart = (date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      0,
      0,
      0,
      0,
    );
  };

  const startHour = 0;
  const endHour = 24;
  const hoursCount = endHour - startHour;
  const visibleStartMinutes = startHour * 60;
  const visibleEndMinutes = endHour * 60;
  const visibleMinutes = visibleEndMinutes - visibleStartMinutes;

  const [hourWidth, setHourWidth] = useState(110);
  const timelineWidth = hoursCount * hourWidth;
  const pxPerMinute = timelineWidth / visibleMinutes;

  useLayoutEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    const update = () => {
      const w = el.clientWidth;
      if (!w) return;
      // Use ceil to avoid leaving a blank gap where the header background stops
      // short of the scroll container width.
      const nextHourWidth = clampNumber(Math.ceil(w / hoursCount), 60, 200);
      setHourWidth(nextHourWidth);
    };

    update();
    const ro = new ResizeObserver(() => update());
    ro.observe(el);
    return () => ro.disconnect();
  }, [hoursCount]);

  const teachers = useMemo(() => {
    const allTeachers = (users || []).filter((u) => u.role === "teacher");

    if (timetableScope === "teacher" && scopeTeacherId) {
      const match = allTeachers.find(
        (u) => String(u.id) === String(scopeTeacherId),
      );
      return match ? [match] : [];
    }

    const term = teacherSearch.trim().toLowerCase();
    if (!term) return allTeachers;
    return allTeachers.filter((u) =>
      (u.full_name || u.email || "").toLowerCase().includes(term),
    );
  }, [users, teacherSearch, timetableScope, scopeTeacherId]);

  const filteredLessons = useMemo(() => {
    const all = lessons || [];
    let scoped = all;

    if (timetableScope === "teacher") {
      if (scopeTeacherId) {
        scoped = all.filter(
          (lesson) => String(lesson?.teacher?.id) === String(scopeTeacherId),
        );
      }
      return dedupeTeacherSessions(scoped);
    }

    if (timetableScope === "class") {
      if (scopeClassId) {
        scoped = all.filter(
          (lesson) =>
            String(lesson?.class_group?.id ?? lesson?.class_group_id) ===
            String(scopeClassId),
        );
      }
      return dedupeTeacherSessions(scoped);
    }

    if (timetableScope === "student") {
      if (!scopeStudentId) return all;
      return all.filter(
        (lesson) => String(lesson?.student?.id) === String(scopeStudentId),
      );
    }

    return all;
  }, [lessons, timetableScope, scopeTeacherId, scopeClassId, scopeStudentId]);

  const dayWindow = useMemo(() => {
    const start = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    );
    const end = new Date(start);
    end.setDate(end.getDate() + 1);
    return { start, end };
  }, [selectedDate]);

  const weekWindow = useMemo(() => {
    const base = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      0,
      0,
      0,
      0,
    );
    // Monday as the first day of week.
    const dow = base.getDay(); // 0..6 (Sun..Sat)
    const diffToMonday = (dow + 6) % 7;
    const start = new Date(base);
    start.setDate(start.getDate() - diffToMonday);
    const end = new Date(start);
    end.setDate(end.getDate() + 7);
    return { start, end };
  }, [selectedDate]);

  const activeWindow = useMemo(() => {
    if (timetableView === "week") return weekWindow;
    return dayWindow;
  }, [timetableView, weekWindow, dayWindow]);

  const activeDays = useMemo(() => {
    const fmt = new Intl.DateTimeFormat(locale, {
      weekday: "long",
      month: "short",
      day: "numeric",
    });

    if (timetableView === "day") {
      return [
        {
          key: toDateInputValue(dayWindow.start),
          date: new Date(dayWindow.start),
          label: fmt.format(dayWindow.start),
        },
      ];
    }

    if (timetableView === "week") {
      return Array.from({ length: 7 }, (_, i) => {
        const date = new Date(weekWindow.start);
        date.setDate(date.getDate() + i);
        return {
          key: toDateInputValue(date),
          date,
          label: fmt.format(date),
        };
      });
    }

    return [];
  }, [timetableView, locale, dayWindow, weekWindow]);

  const lessonsForDay = useMemo(() => {
    const selectedDow = dayWindow.start.getDay();
    return (filteredLessons || []).filter((lesson) => {
      const when = new Date(lesson?.scheduled_at);
      if (Number.isNaN(when.getTime())) return false;

      // One-off lesson: only show on its exact date.
      if (!lesson?.is_recurring_weekly) {
        return when >= dayWindow.start && when < dayWindow.end;
      }

      // Recurring weekly lesson: show every week on the same weekday/time,
      // starting from its initial scheduled date.
      if (when.getDay() !== selectedDow) return false;
      const startDay = normalizeToLocalDayStart(when);
      if (!startDay) return false;
      return dayWindow.start >= startDay;
    });
  }, [filteredLessons, dayWindow]);

  const lessonsByTeacher = useMemo(() => {
    const map = new Map();
    lessonsForDay.forEach((lesson) => {
      const teacherId = lesson?.teacher?.id;
      if (!teacherId) return;
      if (!map.has(teacherId)) map.set(teacherId, []);
      map.get(teacherId).push(lesson);
    });
    return map;
  }, [lessonsForDay]);

  const lessonsForActiveRange = useMemo(() => {
    if (timetableView === "day") return [];

    const occurrences = [];
    const start = activeWindow.start;
    const end = activeWindow.end;

    const makeOccurrenceDate = (baseDate, dayDate) => {
      return new Date(
        dayDate.getFullYear(),
        dayDate.getMonth(),
        dayDate.getDate(),
        baseDate.getHours(),
        baseDate.getMinutes(),
        0,
        0,
      );
    };

    (filteredLessons || []).forEach((lesson) => {
      const teacherId = lesson?.teacher?.id;
      if (!teacherId) return;

      const when = new Date(lesson?.scheduled_at);
      if (Number.isNaN(when.getTime())) return;

      if (!lesson?.is_recurring_weekly) {
        if (when < start || when >= end) return;
        occurrences.push({ lesson, occursAt: when });
        return;
      }

      const startDay = normalizeToLocalDayStart(when);
      if (!startDay) return;

      activeDays.forEach((d) => {
        if (d.date.getDay() !== when.getDay()) return;
        const dayStart = normalizeToLocalDayStart(d.date);
        if (!dayStart) return;
        if (dayStart < startDay) return;
        const occursAt = makeOccurrenceDate(when, d.date);
        if (occursAt < start || occursAt >= end) return;
        occurrences.push({ lesson, occursAt });
      });
    });

    occurrences.sort((a, b) => {
      const diff = a.occursAt - b.occursAt;
      if (diff) return diff;
      const ta = a.lesson?.teacher?.full_name || a.lesson?.teacher?.email || "";
      const tb = b.lesson?.teacher?.full_name || b.lesson?.teacher?.email || "";
      return String(ta).localeCompare(String(tb), locale, {
        sensitivity: "base",
      });
    });

    return occurrences;
  }, [timetableView, filteredLessons, activeWindow, activeDays, locale]);

  // Note: Admin timetable is a single teachers (faculty) view.

  const closeActions = () => {
    if (editorSaving || notifyBusy) return;
    setActionOpen(false);
    setActionLesson(null);
    setActionOccursAt(null);
  };

  const openActionsForLesson = (lesson, occursAtOverride) => {
    if (!lesson) return;
    const when = occursAtOverride
      ? new Date(occursAtOverride)
      : lesson?.scheduled_at
        ? new Date(lesson.scheduled_at)
        : null;

    setActionLesson(lesson);
    setActionOccursAt(when && !Number.isNaN(when.getTime()) ? when : null);
    setActionOpen(true);
    setEditorOpen(false);
    setNotifyOpen(false);
    setNotifyLessonIds([]);
  };

  const openEditorForLesson = (lesson, occursAtOverride) => {
    if (!lesson) return;
    const sessionLessons = resolveSessionLessons(lesson);
    const sessionIds = sessionLessons
      .map((l) => l?.id)
      .filter((id) => Number.isInteger(Number(id)));
    const studentIdsByLessonId = {};
    sessionLessons.forEach((l) => {
      const id = l?.id;
      const sid = l?.student?.id;
      if (id != null && sid != null) {
        studentIdsByLessonId[String(id)] = sid;
      }
    });

    const when = occursAtOverride
      ? new Date(occursAtOverride)
      : lesson?.scheduled_at
        ? new Date(lesson.scheduled_at)
        : null;
    setEditorLessonId(lesson.id);
    setEditorLessonIds(sessionIds);
    setEditorLessonStudentIds(studentIdsByLessonId);
    setEditorTeacherId(fixedTeacherId || lesson?.teacher?.id || null);
    setEditorAssignMode("student");
    setEditorStudentId(String(lesson?.student?.id || ""));
    setEditorClassId(
      lesson?.class_group?.id ? String(lesson.class_group.id) : "",
    );
    setEditorTitle(lesson.title || "");
    setEditorScheduledAt(
      when && !Number.isNaN(when.getTime())
        ? toLocalDateTimeInputValue(when)
        : "",
    );
    setEditorDuration(Number(lesson.duration_minutes) || 60);
    setEditorStatus(lesson.status || "scheduled");
    setEditorIsRecurringWeekly(Boolean(lesson.is_recurring_weekly));
    setEditorOpen(true);
  };

  const openNotifyForLesson = (lesson) => {
    if (!lesson) return;
    const sessionLessons = resolveSessionLessons(lesson);
    const sessionIds = sessionLessons
      .map((l) => l?.id)
      .filter((id) => Number.isInteger(Number(id)));
    setNotifyLessonIds(sessionIds);
    setNotifyStatus(lesson.status === "cancelled" ? "cancelled" : "updated");
    setNotifyMessage("");
    setNotifySuccess(false);
    setNotifySuccessCount(null);
    setNotifyOpen(true);
  };

  const openEditorForSlot = ({
    teacherId,
    minutesFromStart,
    classId,
    dayDate,
  }) => {
    setActionOpen(false);
    setActionLesson(null);
    setActionOccursAt(null);
    setNotifyOpen(false);
    setNotifyLessonIds([]);

    const base =
      dayDate instanceof Date && !Number.isNaN(dayDate.getTime())
        ? dayDate
        : selectedDate;
    const day = new Date(
      base.getFullYear(),
      base.getMonth(),
      base.getDate(),
      0,
      0,
      0,
      0,
    );
    const snapped = Math.round(minutesFromStart / 15) * 15;
    day.setMinutes(snapped);

    const resolvedTeacherId =
      fixedTeacherId ||
      teacherId ||
      (timetableScope === "teacher" && scopeTeacherId
        ? Number(scopeTeacherId)
        : null);

    setEditorLessonId(null);
    setEditorLessonIds([]);
    setEditorLessonStudentIds({});
    setEditorTeacherId(resolvedTeacherId);

    if (timetableScope === "class" && scopeClassId) {
      setEditorAssignMode("class");
      setEditorClassId(String(scopeClassId));
      setEditorStudentId("");
    } else if (timetableScope === "student" && scopeStudentId) {
      setEditorAssignMode("student");
      setEditorStudentId(String(scopeStudentId));
      setEditorClassId("");
    } else {
      setEditorAssignMode(classId ? "class" : "student");
      setEditorStudentId("");
      setEditorClassId(classId ? String(classId) : "");
    }
    setEditorTitle("");
    setEditorScheduledAt(toLocalDateTimeInputValue(day));
    setEditorDuration(60);
    setEditorStatus("scheduled");
    setEditorIsRecurringWeekly(false);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (editorSaving) return;
    setEditorOpen(false);
  };

  const closeNotify = () => {
    if (notifyBusy) return;
    setNotifyOpen(false);
  };

  const sendParentNotification = async (event) => {
    event?.preventDefault?.();
    if (!accessToken || notifyBusy) return;
    const ids = (notifyLessonIds || []).filter((id) => Number(id) > 0);
    if (!ids.length) return;
    setNotifyBusy(true);
    setNotifySuccess(false);
    setNotifySuccessCount(null);
    try {
      let createdCount = 0;
      for (const lessonId of ids) {
        const response = await lessonsService.createLessonParentNotification({
          accessToken,
          lessonId,
          status: notifyStatus,
          message: notifyMessage,
        });

        const createdCountRaw = response?.created_count;
        const delta = Number.isFinite(Number(createdCountRaw))
          ? Number(createdCountRaw)
          : Array.isArray(response?.notifications)
            ? response.notifications.length
            : 1;
        createdCount += delta;
      }

      setNotifyMessage("");
      setNotifySuccess(true);
      setNotifySuccessCount(createdCount);
    } catch (e) {
      onError?.(e.message);
    } finally {
      setNotifyBusy(false);
    }
  };

  const saveEditor = async (event) => {
    event.preventDefault();
    if (!accessToken) return;
    if (!editorTeacherId) return;

    setEditorSaving(true);
    try {
      const scheduledDate = editorScheduledAt
        ? new Date(editorScheduledAt)
        : null;
      if (!scheduledDate || Number.isNaN(scheduledDate.getTime())) {
        throw new Error("scheduled_at is required");
      }

      const payload = {
        title: (editorTitle || "").trim() || "Lesson",
        scheduled_at: scheduledDate.toISOString(),
        duration_minutes: Number(editorDuration) || 60,
        is_recurring_weekly: Boolean(editorIsRecurringWeekly),
        status: editorStatus,
        teacher_id: Number(editorTeacherId),
      };

      let updated;
      if (editorLessonId) {
        const ids = (editorLessonIds || []).filter((id) => Number(id) > 0);
        const effectiveIds = ids.length ? ids : [editorLessonId];

        const updates = [];
        for (const id of effectiveIds) {
          const perLessonStudentId = Number(
            editorLessonStudentIds?.[String(id)] || editorStudentId,
          );
          if (!perLessonStudentId) {
            throw new Error("student_id is required");
          }
          const perPayload = { ...payload, student_id: perLessonStudentId };
          const updatedLesson = await lessonsService.updateLesson(
            accessToken,
            id,
            perPayload,
          );
          updates.push(updatedLesson);
        }

        const byId = new Map(updates.map((u) => [u.id, u]));
        updated = updates[0] || null;
        onLessonsChange?.((current) =>
          (current || []).map((l) => byId.get(l.id) || l),
        );
      } else {
        if (editorAssignMode === "class") {
          const group = (classGroups || []).find(
            (g) => String(g.id) === String(editorClassId),
          );
          const studentIds = (group?.student_ids || [])
            .map((id) => Number(id))
            .filter((n) => Number.isInteger(n) && n > 0);
          if (!studentIds.length) {
            throw new Error("class has no students");
          }

          const created = [];
          for (const sid of studentIds) {
            const createdLesson = await lessonsService.createLesson(
              accessToken,
              {
                ...payload,
                student_id: sid,
                class_group_id: group?.id,
              },
            );
            created.push(createdLesson);
          }
          onLessonsChange?.((current) => [...created, ...(current || [])]);
        } else {
          const studentId = Number(editorStudentId);
          if (!studentId) {
            throw new Error("student_id is required");
          }
          updated = await lessonsService.createLesson(accessToken, {
            ...payload,
            student_id: studentId,
          });
          onLessonsChange?.((current) => [updated, ...(current || [])]);
        }
      }

      setEditorOpen(false);
    } catch (e) {
      onError?.(e.message);
    } finally {
      setEditorSaving(false);
    }
  };

  const deleteEditorLesson = async () => {
    if (!accessToken || !editorLessonId) return;
    setEditorSaving(true);
    try {
      const ids = (editorLessonIds || []).filter((id) => Number(id) > 0);
      const effectiveIds = ids.length ? ids : [editorLessonId];

      for (const id of effectiveIds) {
        await lessonsService.deleteLesson(accessToken, id);
      }

      const idSet = new Set(effectiveIds.map((id) => Number(id)));
      onLessonsChange?.((current) =>
        (current || []).filter((l) => !idSet.has(Number(l.id))),
      );
      setEditorOpen(false);
    } catch (e) {
      onError?.(e.message);
      setEditorSaving(false);
    }
  };

  const shiftDay = (delta) => {
    setSelectedDate((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + delta);
      return next;
    });
  };

  const shiftWeek = (deltaWeeks) => {
    setSelectedDate((current) => {
      const next = new Date(current);
      next.setDate(next.getDate() + deltaWeeks * 7);
      return next;
    });
  };

  const shiftWindow = (delta) => {
    if (timetableView === "week") return shiftWeek(delta);
    return shiftDay(delta);
  };

  const goToToday = () => {
    const now = new Date();
    setSelectedDate(new Date(now.getFullYear(), now.getMonth(), now.getDate()));
  };

  useTimelineStartScroll(canvasRef, hourWidth, [
    selectedDate,
    timetableView,
    timetableScope,
    scopeTeacherId,
    scopeClassId,
    scopeStudentId,
  ]);

  const timelineHours = useMemo(() => {
    return Array.from({ length: hoursCount }, (_, i) => {
      const hour = startHour + i;
      return {
        hour,
        label: `${String(hour).padStart(2, "0")}:00`,
        left: (i + 0.5) * hourWidth,
      };
    });
  }, [hourWidth, hoursCount, startHour]);

  const syncScroll = (source) => {
    if (syncingScrollRef.current) return;
    syncingScrollRef.current = true;
    try {
      if (source === "canvas") {
        if (teachersBodyRef.current && canvasRef.current) {
          teachersBodyRef.current.scrollTop = canvasRef.current.scrollTop;
        }
      } else {
        if (teachersBodyRef.current && canvasRef.current) {
          canvasRef.current.scrollTop = teachersBodyRef.current.scrollTop;
        }
      }
    } finally {
      syncingScrollRef.current = false;
    }
  };

  const headerSubtitle = useMemo(() => {
    if (timetableView === "week") {
      const endInclusive = new Date(weekWindow.end);
      endInclusive.setDate(endInclusive.getDate() - 1);
      const fmt = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      return `${fmt.format(weekWindow.start)} – ${fmt.format(endInclusive)}`;
    }

    return new Intl.DateTimeFormat(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(selectedDate);
  }, [timetableView, locale, selectedDate, weekWindow]);

  const activeSegBtnClass = (mode) => {
    const base = "admin-timetable-topcard__segBtn";
    return timetableView === mode
      ? `${base} admin-timetable-topcard__segBtn--active`
      : base;
  };

  const activeScopeBtnClass = (mode) => {
    const base = "admin-timetable-topcard__segBtn";
    return timetableScope === mode
      ? `${base} admin-timetable-topcard__segBtn--active`
      : base;
  };

  const openNewEventFromHeader = () => {
    openEditorForSlot({
      teacherId: null,
      minutesFromStart: visibleStartMinutes,
      classId: "",
    });
  };

  const weekOccurrencesByDayKey = useMemo(() => {
    if (timetableView !== "week") return new Map();

    const map = new Map();
    activeDays.forEach((d) => map.set(d.key, []));

    lessonsForActiveRange.forEach((entry) => {
      const key = toDateInputValue(entry.occursAt);
      if (!map.has(key)) return;
      map.get(key).push(entry);
    });

    activeDays.forEach((d) => {
      const items = map.get(d.key);
      if (!items) return;
      items.sort((a, b) => a.occursAt - b.occursAt);
    });

    return map;
  }, [timetableView, activeDays, lessonsForActiveRange]);

  const totalLessonsInWeek = useMemo(() => {
    if (timetableView !== "week") return 0;
    let total = 0;
    weekOccurrencesByDayKey.forEach((items) => {
      total += items.length;
    });
    return total;
  }, [timetableView, weekOccurrencesByDayKey]);

  return (
    <section>
      <div className="admin-section-head">
        <h2>{t("adminPage.tabs.timetable")}</h2>
        <button type="button" className="admin-back" onClick={onBack}>
          <i
            className={`fa-solid ${
              dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
            }`}
            aria-hidden="true"
          ></i>
          <span className="sr-only">{t("adminPage.tabs.overview")}</span>
        </button>
      </div>

      <div className="admin-timetable-topcard">
        <div className="admin-timetable-topcard__head">
          <div className="admin-timetable-topcard__title">
            <div className="admin-timetable-topcard__icon" aria-hidden="true">
              <i className="fa-solid fa-calendar-days"></i>
            </div>
            <div>
              <div className="admin-timetable-topcard__titleText">
                {t("adminPage.timetable.calendarTitle")}
              </div>
              <div className="admin-timetable-topcard__subtitle">
                {headerSubtitle}
              </div>
            </div>
          </div>

          <div className="admin-timetable-topcard__actions">
            <button
              type="button"
              className="admin-timetable-topcard__action admin-timetable-topcard__action--primary"
              onClick={openNewEventFromHeader}
            >
              <i className="fa-solid fa-plus" aria-hidden="true"></i>
              <span>{t("adminPage.timetable.addEvent")}</span>
            </button>
            <button
              type="button"
              className="admin-timetable-topcard__action"
              aria-label={t("adminPage.timetable.configure")}
            >
              <i className="fa-solid fa-gear" aria-hidden="true"></i>
              <span>{t("adminPage.timetable.configure")}</span>
            </button>
          </div>
        </div>

        <div className="admin-timetable-topcard__controls">
          <div className="admin-timetable-topcard__dateControls">
            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={() => shiftWindow(-1)}
              aria-label={
                timetableView === "week"
                  ? t("lessons.timetable.previousWeek")
                  : t("adminPage.timetable.previousDay")
              }
            >
              <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
            </button>
            <div className="admin-timetable-topcard__datePill">
              <input
                className="admin-timetable-topcard__dateInput"
                type="date"
                value={toDateInputValue(selectedDate)}
                onChange={(e) => {
                  const parsed = parseDateInput(e.target.value);
                  if (parsed) setSelectedDate(parsed);
                }}
              />
            </div>
            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={() => shiftWindow(1)}
              aria-label={
                timetableView === "week"
                  ? t("lessons.timetable.nextWeek")
                  : t("adminPage.timetable.nextDay")
              }
            >
              <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
            </button>

            <button
              type="button"
              className="admin-timetable-topcard__pillBtn"
              onClick={goToToday}
            >
              {t("lessons.timetable.today")}
            </button>
          </div>

          <div className="admin-timetable-topcard__rightControls">
            {timetableView === "week" && (
              <>
                <div className="admin-timetable-topcard__seg">
                  <button
                    type="button"
                    className={activeScopeBtnClass("teacher")}
                    onClick={() => {
                      setTimetableScope("teacher");
                      setScopeClassId("");
                      setScopeStudentId("");
                    }}
                  >
                    {t("adminPage.timetable.scopeTeacher")}
                  </button>
                  <button
                    type="button"
                    className={activeScopeBtnClass("class")}
                    onClick={() => {
                      setTimetableScope("class");
                      setScopeTeacherId("");
                      setScopeStudentId("");
                    }}
                  >
                    {t("adminPage.timetable.scopeClass")}
                  </button>
                  <button
                    type="button"
                    className={activeScopeBtnClass("student")}
                    onClick={() => {
                      setTimetableScope("student");
                      setScopeTeacherId("");
                      setScopeClassId("");
                    }}
                  >
                    {t("adminPage.timetable.scopeStudent")}
                  </button>
                </div>

                <div className="admin-timetable-topcard__datePill">
                  {timetableScope === "teacher" ? (
                    <select
                      className="admin-timetable-topcard__dateInput"
                      value={scopeTeacherId}
                      onChange={(e) => setScopeTeacherId(e.target.value)}
                    >
                      <option value="">
                        {t("adminPage.timetable.allTeachers")}
                      </option>
                      {teacherOptions.map((teacher) => (
                        <option key={teacher.id} value={String(teacher.id)}>
                          {teacher.full_name || teacher.email}
                        </option>
                      ))}
                    </select>
                  ) : timetableScope === "class" ? (
                    <select
                      className="admin-timetable-topcard__dateInput"
                      value={scopeClassId}
                      onChange={(e) => setScopeClassId(e.target.value)}
                    >
                      <option value="">
                        {t("adminPage.timetable.allClasses")}
                      </option>
                      {(classGroups || []).map((g) => (
                        <option key={g.id} value={String(g.id)}>
                          {g.name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      className="admin-timetable-topcard__dateInput"
                      value={scopeStudentId}
                      onChange={(e) => setScopeStudentId(e.target.value)}
                    >
                      <option value="">
                        {t("adminPage.timetable.allStudents")}
                      </option>
                      {(students || []).map((s) => (
                        <option key={s.id} value={String(s.id)}>
                          {s.full_name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              </>
            )}

            <div className="admin-timetable-topcard__seg">
              <button
                type="button"
                className={activeSegBtnClass("week")}
                onClick={() => {
                  setTeacherSearch("");
                  setTimetableView("week");
                }}
              >
                {t("adminPage.timetable.viewWeek")}
              </button>
              <button
                type="button"
                className={activeSegBtnClass("day")}
                onClick={() => setTimetableView("day")}
              >
                {t("adminPage.timetable.viewDay")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="admin-timetable admin-timetable--full">
        <div className="admin-timetable__toolbar">
          <div className="admin-timetable__meta text-muted">
            {headerSubtitle}
          </div>
        </div>

        {timetableView === "day" ? (
          <div className="admin-timetable__grid">
            <aside className="admin-timetable__teachers">
              <div className="admin-timetable__teachersHead">
                <div className="admin-timetable__teachersTitle">
                  {t("adminPage.timetable.faculty")}
                </div>
                <input
                  className="admin-timetable__search"
                  value={teacherSearch}
                  onChange={(e) => setTeacherSearch(e.target.value)}
                  placeholder={t(
                    "adminPage.timetable.searchFacultyPlaceholder",
                  )}
                />
              </div>
              <div
                className="admin-timetable__teachersBody"
                ref={teachersBodyRef}
                onScroll={() => syncScroll("teachers")}
              >
                {teachers.map((teacher) => {
                  const dayCount = (lessonsByTeacher.get(teacher.id) || [])
                    .length;
                  return (
                    <div
                      className="admin-timetable__teacherRow"
                      key={teacher.id}
                    >
                      <div className="admin-timetable__teacherName">
                        {teacher.full_name || teacher.email}
                      </div>
                      <div className="admin-timetable__teacherCount">
                        {dayCount}{" "}
                        {dayCount === 1
                          ? t("lessons.timetable.item")
                          : t("lessons.timetable.items")}
                      </div>
                    </div>
                  );
                })}
                {!teachers.length && (
                  <div className="admin-timetable__empty">
                    {t("adminPage.dashboard.empty")}
                  </div>
                )}
              </div>
            </aside>

            <div
              className="admin-timetable__canvas"
              ref={canvasRef}
              onScroll={() => syncScroll("canvas")}
              style={{
                "--timetable-width": `${timelineWidth}px`,
                "--timetable-hour": `${hourWidth}px`,
              }}
            >
              <div className="admin-timetable__times">
                <div className="admin-timetable__timesInner">
                  {timelineHours.map((h) => (
                    <div
                      key={h.hour}
                      className="admin-timetable__timeTick"
                      style={{ insetInlineStart: `${h.left}px` }}
                    >
                      <div className="admin-timetable__timeHour">{h.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-timetable__rows">
                {teachers.map((row, rowIndex) => {
                  const teacherIdForRow = row.id;
                  const rowLessons = lessonsByTeacher.get(row.id) || [];

                  return (
                    <div
                      key={row.id}
                      className="admin-timetable__row"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const scrollX = canvasRef.current?.scrollLeft || 0;
                        const xWithScroll = x + scrollX;
                        const minutesFromStart = clampNumber(
                          visibleStartMinutes +
                            Math.round(xWithScroll / pxPerMinute),
                          visibleStartMinutes,
                          visibleEndMinutes,
                        );
                        openEditorForSlot({
                          teacherId: teacherIdForRow,
                          classId: "",
                          minutesFromStart,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        openEditorForSlot({
                          teacherId: teacherIdForRow,
                          classId: "",
                          minutesFromStart: 9 * 60,
                        });
                      }}
                    >
                      {rowLessons.map((lesson) => {
                        const when = new Date(lesson?.scheduled_at);
                        if (Number.isNaN(when.getTime())) return null;
                        const startMinutes =
                          when.getHours() * 60 + when.getMinutes();
                        const duration = Number(lesson.duration_minutes) || 60;

                        const endMinutes = startMinutes + duration;
                        if (
                          endMinutes <= visibleStartMinutes ||
                          startMinutes >= visibleEndMinutes
                        ) {
                          return null;
                        }

                        const clippedStart = Math.max(
                          startMinutes,
                          visibleStartMinutes,
                        );
                        const clippedEnd = Math.min(
                          endMinutes,
                          visibleEndMinutes,
                        );
                        const left =
                          (clippedStart - visibleStartMinutes) * pxPerMinute;
                        const width = clampNumber(
                          (clippedEnd - clippedStart) * pxPerMinute,
                          18,
                          timelineWidth,
                        );
                        const style = {
                          insetInlineStart: `${left}px`,
                          width: `${width}px`,
                        };
                        const colorClass =
                          rowIndex % 3 === 0
                            ? "admin-timetable__event--a"
                            : rowIndex % 3 === 1
                              ? "admin-timetable__event--b"
                              : "admin-timetable__event--c";

                        return (
                          <button
                            key={lesson.id}
                            type="button"
                            className={`admin-timetable__event ${colorClass}`}
                            style={style}
                            onClick={(evt) => {
                              evt.stopPropagation();
                              openActionsForLesson(lesson);
                            }}
                          >
                            <div className="admin-timetable__eventTitle">
                              {lesson.title}
                            </div>
                            <div className="admin-timetable__eventSub">
                              {(() => {
                                const studentName =
                                  lesson?.student?.full_name || "";
                                const persistedClassName =
                                  lesson?.class_group?.name;
                                return persistedClassName || studentName;
                              })()}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="admin-timetable__grid">
            <aside className="admin-timetable__teachers">
              <div className="admin-timetable__teachersHead">
                <div className="admin-timetable__teachersTitle">
                  {t("lessons.timetable.days")}
                </div>
              </div>
              <div
                className="admin-timetable__teachersBody"
                ref={teachersBodyRef}
                onScroll={() => syncScroll("teachers")}
              >
                {activeDays.map((d) => {
                  const count = (weekOccurrencesByDayKey.get(d.key) || [])
                    .length;
                  return (
                    <div className="admin-timetable__teacherRow" key={d.key}>
                      <div className="admin-timetable__teacherName">
                        {d.label}
                      </div>
                      <div className="admin-timetable__teacherCount">
                        {count}{" "}
                        {count === 1
                          ? t("lessons.timetable.item")
                          : t("lessons.timetable.items")}
                      </div>
                    </div>
                  );
                })}
                {!totalLessonsInWeek && (
                  <div className="admin-timetable__empty">
                    {t("adminPage.dashboard.empty")}
                  </div>
                )}
              </div>
            </aside>

            <div
              className="admin-timetable__canvas"
              ref={canvasRef}
              onScroll={() => syncScroll("canvas")}
              style={{
                "--timetable-width": `${timelineWidth}px`,
                "--timetable-hour": `${hourWidth}px`,
              }}
            >
              <div className="admin-timetable__times">
                <div className="admin-timetable__timesInner">
                  {timelineHours.map((h) => (
                    <div
                      key={h.hour}
                      className="admin-timetable__timeTick"
                      style={{ insetInlineStart: `${h.left}px` }}
                    >
                      <div className="admin-timetable__timeHour">{h.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="admin-timetable__rows">
                {activeDays.map((day, rowIndex) => {
                  const rowItems = weekOccurrencesByDayKey.get(day.key) || [];
                  return (
                    <div
                      key={day.key}
                      className="admin-timetable__row"
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const x = e.clientX - rect.left;
                        const scrollX = canvasRef.current?.scrollLeft || 0;
                        const xWithScroll = x + scrollX;
                        const minutesFromStart = clampNumber(
                          visibleStartMinutes +
                            Math.round(xWithScroll / pxPerMinute),
                          visibleStartMinutes,
                          visibleEndMinutes,
                        );
                        openEditorForSlot({
                          teacherId: null,
                          classId: "",
                          minutesFromStart,
                          dayDate: day.date,
                        });
                      }}
                      onKeyDown={(e) => {
                        if (e.key !== "Enter") return;
                        openEditorForSlot({
                          teacherId: null,
                          classId: "",
                          minutesFromStart: 9 * 60,
                          dayDate: day.date,
                        });
                      }}
                    >
                      {rowItems.map(({ lesson, occursAt }, idx) => {
                        const when = new Date(occursAt);
                        if (Number.isNaN(when.getTime())) return null;
                        const startMinutes =
                          when.getHours() * 60 + when.getMinutes();
                        const duration = Number(lesson.duration_minutes) || 60;

                        const endMinutes = startMinutes + duration;
                        if (
                          endMinutes <= visibleStartMinutes ||
                          startMinutes >= visibleEndMinutes
                        ) {
                          return null;
                        }

                        const clippedStart = Math.max(
                          startMinutes,
                          visibleStartMinutes,
                        );
                        const clippedEnd = Math.min(
                          endMinutes,
                          visibleEndMinutes,
                        );
                        const left =
                          (clippedStart - visibleStartMinutes) * pxPerMinute;
                        const width = clampNumber(
                          (clippedEnd - clippedStart) * pxPerMinute,
                          18,
                          timelineWidth,
                        );
                        const style = {
                          insetInlineStart: `${left}px`,
                          width: `${width}px`,
                        };
                        const colorClass =
                          rowIndex % 3 === 0
                            ? "admin-timetable__event--a"
                            : rowIndex % 3 === 1
                              ? "admin-timetable__event--b"
                              : "admin-timetable__event--c";

                        const teacherName =
                          lesson?.teacher?.full_name ||
                          lesson?.teacher?.email ||
                          "";
                        const studentName = lesson?.student?.full_name || "";
                        const persistedClassName = lesson?.class_group?.name;
                        const studentOrClass =
                          persistedClassName || studentName;
                        const subtitle = [teacherName, studentOrClass]
                          .filter(Boolean)
                          .join(" • ");

                        return (
                          <button
                            key={`${lesson.id}-${when.toISOString()}-${idx}`}
                            type="button"
                            className={`admin-timetable__event ${colorClass}`}
                            style={style}
                            onClick={(evt) => {
                              evt.stopPropagation();
                              openActionsForLesson(lesson, when);
                            }}
                          >
                            <div className="admin-timetable__eventTitle">
                              {lesson.title}
                            </div>
                            <div className="admin-timetable__eventSub">
                              {subtitle}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {actionOpen && (
        <div
          className="admin-timetable__modalBackdrop"
          role="presentation"
          onMouseDown={closeActions}
        >
          <div
            className="admin-timetable__modal"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="admin-timetable__modalHead">
              <div className="admin-timetable__modalTitle">
                {actionLesson?.title || t("lessonDetail.idLabel")}
              </div>
              <button
                type="button"
                className="admin-timetable__modalClose"
                onClick={closeActions}
                aria-label={t("sidebar.close")}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <div className="admin-timetable__modalBody">
              <div className="admin-timetable__actions">
                <button
                  type="button"
                  className="btn"
                  onClick={() => {
                    const lesson = actionLesson;
                    const when = actionOccursAt;
                    closeActions();
                    if (lesson) {
                      openEditorForLesson(lesson, when);
                    }
                  }}
                >
                  {t("lessonDetail.edit")}
                </button>

                <button
                  type="button"
                  className="btn primary"
                  onClick={() => {
                    const lesson = actionLesson;
                    closeActions();
                    if (lesson) {
                      openNotifyForLesson(lesson);
                    }
                  }}
                >
                  {t("lessonDetail.notifyParentsTitle")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editorOpen && (
        <div
          className="admin-timetable__modalBackdrop"
          role="presentation"
          onMouseDown={closeEditor}
        >
          <div
            className="admin-timetable__modal"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="admin-timetable__modalHead">
              <div className="admin-timetable__modalTitle">
                {editorLessonId
                  ? t("adminPage.timetable.editLessonTitle")
                  : t("adminPage.timetable.newLessonTitle")}
              </div>
              <button
                type="button"
                className="admin-timetable__modalClose"
                onClick={closeEditor}
                aria-label={t("sidebar.close")}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <form className="admin-timetable__modalBody" onSubmit={saveEditor}>
              {(() => {
                const selectedById = editorClassId
                  ? (classGroups || []).find(
                      (g) => String(g.id) === String(editorClassId),
                    )
                  : null;
                const editingGroup = selectedById || null;
                const isEditingClassLesson = Boolean(
                  editorLessonId && editorClassId,
                );
                const classStudents = Array.isArray(editingGroup?.students)
                  ? editingGroup.students
                  : [];
                const classStudentsLabel = classStudents
                  .map((s) => s?.full_name)
                  .filter(Boolean)
                  .join(", ");

                return (
                  <>
                    <label className="admin-timetable__field">
                      <span className="admin-timetable__label">
                        {t("adminPage.timetable.fields.teacher")}
                      </span>
                      {editorLessonId ? (
                        <input
                          className="admin-timetable__input"
                          value={
                            (users || []).find((u) => u.id === editorTeacherId)
                              ?.full_name ||
                            (users || []).find((u) => u.id === editorTeacherId)
                              ?.email ||
                            ""
                          }
                          readOnly
                        />
                      ) : (
                        <select
                          className="admin-timetable__input"
                          value={editorTeacherId ? String(editorTeacherId) : ""}
                          onChange={(e) => {
                            const next = Number(e.target.value);
                            setEditorTeacherId(
                              Number.isInteger(next) && next > 0 ? next : null,
                            );
                          }}
                          required
                        >
                          <option value="">
                            {t("adminPage.timetable.selectPlaceholder")}
                          </option>
                          {teacherOptions.map((teacher) => (
                            <option key={teacher.id} value={String(teacher.id)}>
                              {teacher.full_name || teacher.email}
                            </option>
                          ))}
                        </select>
                      )}
                    </label>

                    {editorLessonId && (
                      <label className="admin-timetable__field">
                        <span className="admin-timetable__label">
                          {t("adminPage.timetable.fields.class")}
                        </span>
                        <input
                          className="admin-timetable__input"
                          value={(() => {
                            if (editingGroup?.name) return editingGroup.name;
                            return "—";
                          })()}
                          readOnly
                        />
                      </label>
                    )}

                    {isEditingClassLesson && (
                      <label className="admin-timetable__field">
                        <span className="admin-timetable__label">
                          {t("adminPage.timetable.fields.students")}
                        </span>
                        <textarea
                          className="admin-timetable__input"
                          value={classStudentsLabel || "—"}
                          readOnly
                          rows={3}
                        />
                      </label>
                    )}

                    {!editorLessonId && (
                      <label className="admin-timetable__field">
                        <span className="admin-timetable__label">
                          {t("adminPage.timetable.fields.assignTo")}
                        </span>
                        <select
                          className="admin-timetable__input"
                          value={editorAssignMode}
                          onChange={(e) => {
                            const next =
                              e.target.value === "class" ? "class" : "student";
                            setEditorAssignMode(next);
                            setEditorStudentId("");
                            if (next !== "class") {
                              setEditorClassId("");
                            }
                          }}
                        >
                          <option value="student">
                            {t("adminPage.timetable.assignModes.student")}
                          </option>
                          <option value="class">
                            {t("adminPage.timetable.assignModes.class")}
                          </option>
                        </select>
                      </label>
                    )}

                    {(editorLessonId || editorAssignMode === "student") &&
                      !isEditingClassLesson && (
                        <label className="admin-timetable__field">
                          <span className="admin-timetable__label">
                            {t("adminPage.timetable.fields.student")}
                          </span>
                          <select
                            className="admin-timetable__input"
                            value={editorStudentId}
                            onChange={(e) => setEditorStudentId(e.target.value)}
                            required
                          >
                            <option value="">
                              {t("adminPage.timetable.selectPlaceholder")}
                            </option>
                            {(students || []).map((s) => (
                              <option key={s.id} value={String(s.id)}>
                                {s.full_name}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}

                    {!editorLessonId && editorAssignMode === "class" && (
                      <label className="admin-timetable__field">
                        <span className="admin-timetable__label">
                          {t("adminPage.timetable.fields.class")}
                        </span>
                        <select
                          className="admin-timetable__input"
                          value={editorClassId}
                          onChange={(e) => setEditorClassId(e.target.value)}
                          required
                        >
                          <option value="">
                            {t("adminPage.timetable.selectPlaceholder")}
                          </option>
                          {(classGroups || []).map((g) => (
                            <option key={g.id} value={String(g.id)}>
                              {g.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    )}

                    <label className="admin-timetable__field">
                      <span className="admin-timetable__label">
                        {t("adminPage.timetable.fields.title")}
                      </span>
                      <input
                        className="admin-timetable__input"
                        value={editorTitle}
                        onChange={(e) => setEditorTitle(e.target.value)}
                        placeholder={t(
                          "adminPage.timetable.lessonTitlePlaceholder",
                        )}
                      />
                    </label>
                  </>
                );
              })()}

              <div className="admin-timetable__grid2">
                <label className="admin-timetable__field">
                  <span className="admin-timetable__label">
                    {t("adminPage.timetable.fields.start")}
                  </span>
                  <input
                    className="admin-timetable__input"
                    type="datetime-local"
                    value={editorScheduledAt}
                    onChange={(e) => setEditorScheduledAt(e.target.value)}
                    required
                  />
                </label>
                <label className="admin-timetable__field">
                  <span className="admin-timetable__label">
                    {t("adminPage.timetable.fields.duration")}
                  </span>
                  <select
                    className="admin-timetable__input"
                    value={String(editorDuration)}
                    onChange={(e) => setEditorDuration(Number(e.target.value))}
                    required
                  >
                    {Array.from({ length: 16 }, (_, i) => (i + 1) * 15).map(
                      (m) => (
                        <option key={m} value={String(m)}>
                          {m} {t("adminPage.timetable.minutesShort")}
                        </option>
                      ),
                    )}
                  </select>
                </label>
              </div>

              <label className="admin-timetable__field">
                <span className="admin-timetable__label">
                  {t("adminPage.timetable.fields.status")}
                </span>
                <select
                  className="admin-timetable__input"
                  value={editorStatus}
                  onChange={(e) => setEditorStatus(e.target.value)}
                >
                  <option value="scheduled">
                    {t("lessons.statusScheduled")}
                  </option>
                  <option value="completed">
                    {t("lessons.statusCompleted")}
                  </option>
                  <option value="cancelled">
                    {t("lessons.statusCancelled")}
                  </option>
                </select>
              </label>

              <label className="admin-timetable__field checkbox">
                <input
                  type="checkbox"
                  checked={editorIsRecurringWeekly}
                  onChange={(e) => setEditorIsRecurringWeekly(e.target.checked)}
                />
                <span>{t("adminPage.timetable.permanentWeekly")}</span>
              </label>

              <div className="admin-timetable__actions">
                {editorLessonId ? (
                  <button
                    type="button"
                    className="btn danger"
                    onClick={deleteEditorLesson}
                    disabled={editorSaving}
                  >
                    {t("adminPage.actions.delete")}
                  </button>
                ) : (
                  <span />
                )}
                <button
                  type="submit"
                  className="btn primary"
                  disabled={editorSaving}
                >
                  {editorSaving
                    ? t("adminPage.actions.saving")
                    : t("adminPage.actions.save")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {notifyOpen && (
        <div
          className="admin-timetable__modalBackdrop"
          role="presentation"
          onMouseDown={closeNotify}
        >
          <div
            className="admin-timetable__modal"
            role="dialog"
            aria-modal="true"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="admin-timetable__modalHead">
              <div className="admin-timetable__modalTitle">
                {t("lessonDetail.notifyParentsTitle")}
              </div>
              <button
                type="button"
                className="admin-timetable__modalClose"
                onClick={closeNotify}
                aria-label={t("sidebar.close")}
              >
                <i className="fa-solid fa-xmark" aria-hidden="true"></i>
              </button>
            </div>

            <form
              className="admin-timetable__modalBody"
              onSubmit={sendParentNotification}
            >
              <label className="admin-timetable__field">
                <span className="admin-timetable__label">
                  {t("lessonDetail.notifyParentsStatus")}
                </span>
                <select
                  className="admin-timetable__input"
                  value={notifyStatus}
                  onChange={(e) => setNotifyStatus(e.target.value)}
                  disabled={notifyBusy}
                >
                  <option value="cancelled">
                    {t("dashboard.parent.notifications.status.cancelled")}
                  </option>
                  <option value="postponed">
                    {t("dashboard.parent.notifications.status.postponed")}
                  </option>
                  <option value="updated">
                    {t("dashboard.parent.notifications.status.updated")}
                  </option>
                </select>
              </label>

              <label className="admin-timetable__field">
                <span className="admin-timetable__label">
                  {t("lessonDetail.notifyParentsMessage")}
                </span>
                <textarea
                  className="admin-timetable__input"
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  rows={4}
                  disabled={notifyBusy}
                />
              </label>

              {notifySuccess ? (
                <div className="admin-timetable__label" role="status">
                  {typeof notifySuccessCount === "number"
                    ? t("lessonDetail.notifyParentsSuccessCount", {
                        count: notifySuccessCount,
                      })
                    : t("lessonDetail.notifyParentsSuccess")}
                </div>
              ) : null}

              <div className="admin-timetable__actions">
                <span />
                <button
                  type="submit"
                  className="btn primary"
                  disabled={notifyBusy || !notifyMessage.trim()}
                >
                  {notifyBusy
                    ? t("lessonDetail.notifyParentsSending")
                    : t("lessonDetail.notifyParentsSend")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}

function ClassesTab({
  t,
  dir,
  locale,
  users,
  students,
  classGroups,
  accessToken,
  onBack,
  onClassGroupsChange,
  onError,
}) {
  const studentOptions = useMemo(() => {
    return [...(students || [])].sort((a, b) =>
      String(a.full_name || "").localeCompare(
        String(b.full_name || ""),
        locale,
        {
          sensitivity: "base",
        },
      ),
    );
  }, [students, locale]);

  const [form, setForm] = useState({
    name: "",
    student_ids: [],
  });
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [studentSearch, setStudentSearch] = useState("");
  const [dragOver, setDragOver] = useState(null);
  const editorRef = useRef(null);

  const studentsById = useMemo(
    () => new Map((students || []).map((s) => [s.id, s])),
    [students],
  );

  const selectedStudents = useMemo(
    () =>
      (form.student_ids || [])
        .map((id) => studentsById.get(Number(id)))
        .filter(Boolean),
    [form.student_ids, studentsById],
  );

  const availableStudents = useMemo(() => {
    const selected = new Set((form.student_ids || []).map(Number));
    const query = studentSearch.trim().toLowerCase();
    return studentOptions.filter(
      (s) =>
        !selected.has(s.id) &&
        (!query ||
          String(s.full_name || "")
            .toLowerCase()
            .includes(query) ||
          String(s.parent_email || "")
            .toLowerCase()
            .includes(query)),
    );
  }, [form.student_ids, studentOptions, studentSearch]);

  const addStudent = (id) => {
    if (!Number.isInteger(id) || !studentsById.has(id)) return;
    setForm((c) =>
      c.student_ids.map(Number).includes(id)
        ? c
        : { ...c, student_ids: [...c.student_ids, id] },
    );
  };

  const removeStudent = (id) => {
    setForm((c) => ({
      ...c,
      student_ids: c.student_ids.filter((sid) => Number(sid) !== id),
    }));
  };

  const resetForm = () => {
    setForm({ name: "", student_ids: [] });
    setEditingId(null);
    setStudentSearch("");
  };

  const startEdit = (group) => {
    if (!group) return;
    setEditingId(group.id);
    setStudentSearch("");
    setForm({
      name: group.name || "",
      student_ids: Array.isArray(group.student_ids) ? group.student_ids : [],
    });
    editorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const saveClass = async (event) => {
    event.preventDefault();
    if (!accessToken) return;

    const name = String(form.name || "").trim();
    if (!name) {
      onError?.(t("adminPage.classes.errors.nameRequired"));
      return;
    }

    const studentIds = (form.student_ids || [])
      .map((id) => Number(id))
      .filter((n) => Number.isInteger(n) && n > 0);

    setSaving(true);
    try {
      if (editingId) {
        const updated = await classesService.updateClassGroup(
          accessToken,
          editingId,
          {
            name,
            student_ids: studentIds,
          },
        );
        onClassGroupsChange?.((current) =>
          (current || []).map((g) => (g.id === editingId ? updated : g)),
        );
      } else {
        const created = await classesService.createClassGroup(accessToken, {
          name,
          student_ids: studentIds,
        });
        onClassGroupsChange?.((current) => [created, ...(current || [])]);
      }

      resetForm();
    } catch (e) {
      onError?.(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteClass = async (groupId) => {
    if (!accessToken) return;
    const confirmed = window.confirm(t("adminPage.classes.confirmDelete"));
    if (!confirmed) return;

    setSaving(true);
    try {
      await classesService.deleteClassGroup(accessToken, groupId);
      onClassGroupsChange?.((current) =>
        (current || []).filter((g) => g.id !== groupId),
      );
      if (editingId === groupId) {
        resetForm();
      }
    } catch (e) {
      onError?.(e.message);
    } finally {
      setSaving(false);
    }
  };

  const classesSorted = useMemo(() => {
    return [...(classGroups || [])].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), locale, {
        sensitivity: "base",
      }),
    );
  }, [classGroups, locale]);

  return (
    <section>
      <div className="admin-section-head">
        <h2>{t("adminPage.tabs.classes")}</h2>
        <button type="button" className="admin-back" onClick={onBack}>
          <i
            className={`fa-solid ${
              dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
            }`}
            aria-hidden="true"
          ></i>
          <span className="sr-only">{t("adminPage.tabs.overview")}</span>
        </button>
      </div>

      <form
        className="admin-panel class-editor"
        ref={editorRef}
        onSubmit={saveClass}
      >
        <div className="class-editor__head">
          <div>
            <h3>
              {editingId
                ? t("adminPage.classes.panelTitleEdit")
                : t("adminPage.classes.panelTitleCreate")}
            </h3>
            <p className="text-muted">{t("adminPage.classEditor.subtitle")}</p>
          </div>
          <label className="class-editor__name">
            <span>{t("adminPage.classes.fields.name")}</span>
            <input
              value={form.name}
              onChange={(e) => setForm((c) => ({ ...c, name: e.target.value }))}
              placeholder={t("adminPage.classes.classNamePlaceholder")}
              required
            />
          </label>
        </div>

        <div className="class-editor__body">
          <section
            className={`class-editor__zone${dragOver === "class" ? " is-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver("class");
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              addStudent(Number(e.dataTransfer.getData("text/plain")));
            }}
            aria-label={t("adminPage.classEditor.inClass")}
          >
            <div className="class-editor__zone-head">
              <h4>{t("adminPage.classEditor.inClass")}</h4>
              <span className="class-editor__count">
                {selectedStudents.length}
              </span>
            </div>
            {selectedStudents.length ? (
              <ul className="class-editor__cards">
                {selectedStudents.map((student) => (
                  <li
                    key={student.id}
                    className="class-editor__card"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", String(student.id));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    <span className="class-editor__avatar" aria-hidden="true">
                      {getInitials(student.full_name)}
                    </span>
                    <span className="class-editor__info">
                      <strong>{student.full_name}</strong>
                      <small>
                        {student.age
                          ? `${student.age} ${t("adminPage.students.ageYears")}`
                          : student.parent_email || ""}
                      </small>
                    </span>
                    <button
                      type="button"
                      className="class-editor__remove"
                      onClick={() => removeStudent(student.id)}
                      aria-label={`${t("adminPage.classEditor.remove")} ${student.full_name}`}
                      title={t("adminPage.classEditor.remove")}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="class-editor__empty">
                <i className="fa-solid fa-hand-pointer" aria-hidden="true" />
                <p>{t("adminPage.classEditor.dropHint")}</p>
              </div>
            )}
          </section>

          <section
            className={`class-editor__pool${dragOver === "pool" ? " is-over" : ""}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver("pool");
            }}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(null);
              removeStudent(Number(e.dataTransfer.getData("text/plain")));
            }}
            aria-label={t("adminPage.classEditor.available")}
          >
            <div className="class-editor__zone-head">
              <h4>{t("adminPage.classEditor.available")}</h4>
              <span className="class-editor__count">
                {availableStudents.length}
              </span>
            </div>
            <div className="class-editor__search">
              <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
              <input
                type="search"
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                placeholder={t("adminPage.classEditor.search")}
                aria-label={t("adminPage.classEditor.search")}
              />
            </div>
            {availableStudents.length ? (
              <ul className="class-editor__list">
                {availableStudents.map((student) => (
                  <li
                    key={student.id}
                    className="class-editor__item"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/plain", String(student.id));
                      e.dataTransfer.effectAllowed = "move";
                    }}
                  >
                    <i
                      className="fa-solid fa-grip-vertical class-editor__grip"
                      aria-hidden="true"
                    />
                    <span
                      className="class-editor__avatar class-editor__avatar--sm"
                      aria-hidden="true"
                    >
                      {getInitials(student.full_name)}
                    </span>
                    <span className="class-editor__info">
                      <strong>{student.full_name}</strong>
                      {student.age ? (
                        <small>
                          {student.age} {t("adminPage.students.ageYears")}
                        </small>
                      ) : null}
                    </span>
                    <button
                      type="button"
                      className="class-editor__add"
                      onClick={() => addStudent(student.id)}
                      aria-label={`${t("adminPage.classEditor.add")} ${student.full_name}`}
                      title={t("adminPage.classEditor.add")}
                    >
                      <i className="fa-solid fa-plus" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="class-editor__none">
                {studentSearch
                  ? t("adminPage.classEditor.noResults")
                  : t("adminPage.classEditor.allAdded")}
              </p>
            )}
          </section>
        </div>

        <div className="class-editor__footer">
          {(editingId || form.name || form.student_ids.length > 0) && (
            <button
              type="button"
              className="btn ghost"
              onClick={resetForm}
              disabled={saving}
            >
              {t("adminPage.actions.cancel")}
            </button>
          )}
          <button type="submit" className="btn primary" disabled={saving}>
            {saving
              ? t("adminPage.actions.saving")
              : editingId
                ? t("adminPage.actions.save")
                : t("adminPage.actions.create")}
          </button>
        </div>
      </form>

      <div className="class-table">
        <div className="class-table__head">
          <span>{t("adminPage.classes.table.class")}</span>
          <span>{t("adminPage.classes.table.students")}</span>
          <span>{t("adminPage.classes.table.actions")}</span>
        </div>
        {classesSorted.length ? (
          classesSorted.map((group) => {
            const members = (group.student_ids || [])
              .map((id) => studentsById.get(id))
              .filter(Boolean);
            return (
              <div
                className={`class-table__row${
                  editingId === group.id ? " is-editing" : ""
                }`}
                key={group.id}
              >
                <span className="class-table__name">{group.name}</span>
                <span className="class-table__members">
                  <span className="class-table__stack" aria-hidden="true">
                    {members.slice(0, 4).map((student) => (
                      <span key={student.id} title={student.full_name}>
                        {getInitials(student.full_name)}
                      </span>
                    ))}
                  </span>
                  <span>{(group.student_ids || []).length}</span>
                </span>
                <span className="class-table__actions">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => startEdit(group)}
                    disabled={saving}
                  >
                    <i className="fa-solid fa-pen" aria-hidden="true" />{" "}
                    {t("adminPage.actions.edit")}
                  </button>
                  <button
                    type="button"
                    className="btn danger"
                    onClick={() => deleteClass(group.id)}
                    disabled={saving}
                  >
                    <i className="fa-solid fa-trash" aria-hidden="true" />{" "}
                    {t("adminPage.actions.delete")}
                  </button>
                </span>
              </div>
            );
          })
        ) : (
          <p className="text-muted class-table__empty">
            {t("adminPage.classEditor.noClasses")}
          </p>
        )}
      </div>
    </section>
  );
}

function tabFromPathname(pathname) {
  if (!pathname) return "overview";
  if (pathname === "/dashboard" || pathname.startsWith("/dashboard/")) {
    if (pathname.startsWith("/dashboard/packs/")) return "packs";
    return "overview";
  }
  if (pathname === "/users") return "users";
  if (pathname === "/packs" || pathname.startsWith("/packs/")) return "packs";
  if (pathname === "/admin/students") return "students";
  if (pathname === "/admin/timetable") return "timetable";
  if (pathname === "/admin/pending-teachers") return "pendingTeachers";
  if (pathname === "/admin/classes") return "classes";
  if (pathname === "/admin/payments") return "payments";
  if (pathname === "/admin/teachers") return "teachers";
  if (pathname === "/admin/support") return "support";
  return "overview";
}

function pathForTab(tab) {
  switch (tab) {
    case "overview":
      return "/dashboard";
    case "users":
      return "/users";
    case "students":
      return "/admin/students";
    case "timetable":
      return "/admin/timetable";
    case "pendingTeachers":
      return "/admin/pending-teachers";
    case "classes":
      return "/admin/classes";
    case "packs":
      return "/packs";
    case "payments":
      return "/admin/payments";
    case "teachers":
      return "/admin/teachers";
    case "support":
      return "/admin/support";
    default:
      return "/dashboard";
  }
}

export default function Admin({ forcedTab } = {}) {
  const { user, accessToken } = useAuth();
  const { t, language } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const locale = language === "ar" ? "ar-EG-u-nu-latn" : language || "en";
  const dir = language === "ar" ? "rtl" : "ltr";
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [timetableTeachers, setTimetableTeachers] = useState([]);
  const [students, setStudents] = useState([]);
  const [timetableStudents, setTimetableStudents] = useState([]);
  const [packs, setPacks] = useState([]);
  const [payments, setPayments] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({});
  // Total item counts reported by the paginated list endpoints.
  const [listCounts, setListCounts] = useState({});
  const [lessons, setLessons] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [userFilter, setUserFilter] = useState("all");
  const [replyDraft, setReplyDraft] = useState(null);
  const [sendingReply, setSendingReply] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersTotalPages, setUsersTotalPages] = useState(1);
  const [studentsPage, setStudentsPage] = useState(1);
  const [studentsTotalPages, setStudentsTotalPages] = useState(1);
  const [lessonsPage, setLessonsPage] = useState(1);
  const [lessonsTotalPages, setLessonsTotalPages] = useState(1);
  const [packsPage, setPacksPage] = useState(1);
  const [packsTotalPages, setPacksTotalPages] = useState(1);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(1);
  const [supportPage, setSupportPage] = useState(1);
  const [supportTotalPages, setSupportTotalPages] = useState(1);

  const lessonsForAdminList = useMemo(() => {
    const sessionLessons = dedupeTeacherSessions(lessons);
    const keyToRepresentative = new Map();
    const keyToStudentIds = new Map();

    for (const lesson of lessons || []) {
      const key = getTeacherSessionKey(lesson);
      if (!key) continue;
      if (!keyToRepresentative.has(key)) {
        keyToRepresentative.set(key, lesson);
      }

      const studentId = lesson?.student?.id;
      if (!studentId) continue;
      if (!keyToStudentIds.has(key)) keyToStudentIds.set(key, new Set());
      keyToStudentIds.get(key).add(String(studentId));
    }

    return sessionLessons.map((lesson) => {
      const key = getTeacherSessionKey(lesson);
      const representative = key ? keyToRepresentative.get(key) : null;
      const studentCount = key ? keyToStudentIds.get(key)?.size || 0 : 0;

      return {
        ...(representative || lesson),
        __sessionKey: key,
        __studentCount: studentCount,
      };
    });
  }, [lessons]);

  const [teachers, setTeachers] = useState([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [teachersError, setTeachersError] = useState(null);
  const [teachersPage, setTeachersPage] = useState(1);
  const [teachersTotalPages, setTeachersTotalPages] = useState(1);
  const [teachersStatusFilter, setTeachersStatusFilter] = useState("all");

  const getSupportSenderEmail = useCallback((message) => {
    if (!message) return "";
    if (typeof message.sender === "string") return message.sender;
    return message.sender?.email || "";
  }, []);

  const roleOptions = useMemo(
    () => [
      { value: "superadmin", label: t("adminPage.roles.superadmin") },
      { value: "admin", label: t("adminPage.roles.admin") },
      { value: "support", label: t("adminPage.roles.support") },
      { value: "accounting", label: t("adminPage.roles.accounting") },
      { value: "teacher", label: t("adminPage.roles.teacher") },
      { value: "parent", label: t("adminPage.roles.parent") },
      { value: "student", label: t("adminPage.roles.student") },
    ],
    [t],
  );

  // Superadmins are not listed in the filter.
  const userFilters = useMemo(
    () => [
      { value: "all", label: t("adminPage.users.filterAll") },
      ...roleOptions.filter((role) => role.value !== "superadmin"),
    ],
    [roleOptions, t],
  );

  const paymentStatusLabels = useMemo(
    () => ({
      pending: t("adminPage.payments.statuses.pending"),
      completed: t("adminPage.payments.statuses.completed"),
      failed: t("adminPage.payments.statuses.failed"),
      refunded: t("adminPage.payments.statuses.refunded"),
    }),
    [t],
  );

  const lessonStatusLabels = useMemo(
    () => ({
      scheduled: t("adminPage.lessons.status.scheduled"),
      completed: t("adminPage.lessons.status.completed"),
      cancelled: t("adminPage.lessons.status.cancelled"),
    }),
    [t],
  );

  const unreadCount = useMemo(
    () => (messages || []).filter((m) => !m.is_read).length,
    [messages],
  );

  useEffect(() => {
    if (activeTab !== "support" && replyDraft) {
      setReplyDraft(null);
      setSendingReply(false);
    }
  }, [activeTab, replyDraft]);

  const filteredUsers = useMemo(() => {
    if (userFilter === "all") return users;
    return users.filter((u) => u.role === userFilter);
  }, [users, userFilter]);

  const isAdmin = useMemo(() => {
    if (!user) return false;
    return ["admin", "superadmin", "support", "accounting"].includes(user.role);
  }, [user]);

  const resolvedTab = useMemo(() => {
    if (forcedTab) return forcedTab;
    return tabFromPathname(location.pathname);
  }, [forcedTab, location.pathname]);

  useEffect(() => {
    if (resolvedTab !== activeTab) {
      setActiveTab(resolvedTab);
    }
  }, [activeTab, resolvedTab]);

  const goToOverview = useCallback(() => {
    navigate("/dashboard");
  }, [navigate]);

  const navigateToTab = useCallback(
    (tab) => {
      if (!tab || tab === "overview") {
        goToOverview();
        return;
      }

      if (tab === "teachers") {
        setTeachersPage(1);
      }

      navigate(pathForTab(tab));
    },
    [goToOverview, navigate],
  );

  useEffect(() => {
    if (activeTab === "lessons") {
      navigateToTab("timetable");
    }
  }, [activeTab, navigateToTab]);

  useEffect(() => {
    if (!isAdmin || !accessToken) {
      setLoading(false);
      return;
    }

    let ignore = false;
    async function loadAll() {
      setLoading(true);
      setError(null);
      try {
        const [
          usersRes,
          timetableTeachersRes,
          studentsRes,
          timetableStudentsRes,
          packsRes,
          paymentsRes,
          messagesRes,
          statsRes,
          lessonsRes,
          catalogRes,
          classesRes,
        ] = await Promise.all([
          adminService.fetchUsers(accessToken, { page: 1, role: userFilter }),
          // Timetable needs a stable list of teachers regardless of the Users tab filter.
          adminService.fetchUsers(accessToken, { role: "teacher" }),
          adminService.fetchStudents(accessToken, { page: 1 }),
          // Omitting page returns every student for the timetable lesson selector.
          adminService.fetchStudents(accessToken),
          adminService.fetchPacks(accessToken, { page: 1 }),
          adminService.fetchPayments(accessToken, { page: 1 }),
          adminService.fetchSupport(accessToken, { page: 1 }),
          adminService.fetchStatistics(accessToken),
          // Don't paginate lessons here: the timetable needs the current week/month,
          // and page=1 returns only the oldest items (often making the timetable look empty).
          lessonsService.fetchLessons({ accessToken }),
          lessonsService.fetchCatalog(),
          classesService
            .fetchClasses({ accessToken, params: { page: 1 } })
            .catch(() => ({ classes: [], page: 1, total_pages: 1 })),
        ]);

        if (ignore) return;
        setUsers(usersRes.users || []);
        setTimetableTeachers(timetableTeachersRes.users || []);
        setUsersPage(usersRes.page || 1);
        setUsersTotalPages(usersRes.total_pages || 1);

        setStudents(studentsRes.students || []);
        setStudentsPage(studentsRes.page || 1);
        setStudentsTotalPages(studentsRes.total_pages || 1);
        setTimetableStudents(timetableStudentsRes.students || []);

        setPacks(packsRes.packs || []);
        setPacksPage(packsRes.page || 1);
        setPacksTotalPages(packsRes.total_pages || 1);

        setPayments(paymentsRes.payments || []);
        setPaymentsPage(paymentsRes.page || 1);
        setPaymentsTotalPages(paymentsRes.total_pages || 1);

        setMessages(messagesRes.messages || []);
        setSupportPage(messagesRes.page || 1);
        setSupportTotalPages(messagesRes.total_pages || 1);
        setStats(statsRes || {});

        setLessons(lessonsRes.lessons || []);
        setLessonsPage(lessonsRes.page || 1);
        setLessonsTotalPages(lessonsRes.total_pages || 1);

        setSubjects(catalogRes.subjects || []);
        setClassGroups(classesRes.classes || []);
        const countOf = (res, key) =>
          Number.isFinite(res?.count) ? res.count : (res?.[key] || []).length;
        setListCounts({
          users: countOf(usersRes, "users"),
          students: countOf(studentsRes, "students"),
          teachers: countOf(timetableTeachersRes, "users"),
          classes: countOf(classesRes, "classes"),
          packs: countOf(packsRes, "packs"),
          payments: countOf(paymentsRes, "payments"),
        });
      } catch (loadError) {
        if (!ignore) {
          setError(loadError.message);
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    loadAll();
    return () => {
      ignore = true;
    };
  }, [isAdmin, accessToken, userFilter]);

  const loadUsersPage = useCallback(
    async (nextPage, roleOverride) => {
      if (!accessToken) return;
      try {
        const effectiveRole = roleOverride;
        const res = await adminService.fetchUsers(accessToken, {
          page: nextPage,
          role: effectiveRole,
        });
        setUsers(res.users || []);
        setUsersPage(res.page || nextPage || 1);
        setUsersTotalPages(res.total_pages || 1);
      } catch (e) {
        setError(e.message);
      }
    },
    [accessToken],
  );

  useEffect(() => {
    if (!isAdmin || !accessToken) {
      return;
    }
    setUsersPage(1);
    loadUsersPage(1, userFilter);
  }, [isAdmin, accessToken, userFilter, loadUsersPage]);

  const loadStudentsPage = useCallback(
    async (nextPage) => {
      if (!accessToken) return;
      try {
        const res = await adminService.fetchStudents(accessToken, {
          page: nextPage,
        });
        setStudents(res.students || []);
        setStudentsPage(res.page || nextPage || 1);
        setStudentsTotalPages(res.total_pages || 1);
      } catch (e) {
        setError(e.message);
      }
    },
    [accessToken],
  );

  const loadLessonsPage = useCallback(
    async (nextPage) => {
      if (!accessToken) return;
      try {
        const res = await lessonsService.fetchLessons({
          accessToken,
          params: { page: nextPage, session: 1 },
        });
        setLessons(res.lessons || []);
        setLessonsPage(res.page || nextPage || 1);
        setLessonsTotalPages(res.total_pages || 1);
      } catch (e) {
        setError(e.message);
      }
    },
    [accessToken],
  );

  const loadPacksPage = useCallback(
    async (nextPage) => {
      if (!accessToken) return;
      try {
        const res = await adminService.fetchPacks(accessToken, {
          page: nextPage,
        });
        setPacks(res.packs || []);
        setPacksPage(res.page || nextPage || 1);
        setPacksTotalPages(res.total_pages || 1);
      } catch (e) {
        setError(e.message);
      }
    },
    [accessToken],
  );

  const loadPaymentsPage = useCallback(
    async (nextPage) => {
      if (!accessToken) return;
      try {
        const res = await adminService.fetchPayments(accessToken, {
          page: nextPage,
        });
        setPayments(res.payments || []);
        setPaymentsPage(res.page || nextPage || 1);
        setPaymentsTotalPages(res.total_pages || 1);
      } catch (e) {
        setError(e.message);
      }
    },
    [accessToken],
  );

  const loadSupportPage = useCallback(
    async (nextPage) => {
      if (!accessToken) return;
      try {
        const res = await adminService.fetchSupport(accessToken, {
          page: nextPage,
        });
        setMessages(res.messages || []);
        setSupportPage(res.page || nextPage || 1);
        setSupportTotalPages(res.total_pages || 1);
      } catch (e) {
        setError(e.message);
      }
    },
    [accessToken],
  );

  const loadTeachersPage = useCallback(
    async (nextPage) => {
      if (!accessToken) return;
      setTeachersLoading(true);
      setTeachersError(null);
      try {
        const isVerifiedParam =
          teachersStatusFilter === "verified"
            ? true
            : teachersStatusFilter === "pending"
              ? false
              : undefined;
        const res = await adminService.fetchUsers(accessToken, {
          page: nextPage,
          role: "teacher",
          is_verified: isVerifiedParam,
        });
        setTeachers(res.users || []);
        setTeachersPage(res.page || nextPage || 1);
        setTeachersTotalPages(res.total_pages || 1);
      } catch (e) {
        setTeachersError(e.message);
      } finally {
        setTeachersLoading(false);
      }
    },
    [accessToken, teachersStatusFilter],
  );

  useEffect(() => {
    if (!isAdmin || !accessToken) {
      return;
    }
    if (activeTab !== "teachers") {
      return;
    }
    loadTeachersPage(teachersPage || 1);
  }, [activeTab, isAdmin, accessToken, teachersPage, loadTeachersPage]);

  const handleUserChange = async (targetId, updates) => {
    if (!accessToken) return;
    try {
      await adminService.updateUser(accessToken, {
        userId: targetId,
        ...updates,
      });
      setUsers((current) =>
        current.map((item) =>
          item.id === targetId ? { ...item, ...updates } : item,
        ),
      );
      setTeachers((current) =>
        current.map((item) =>
          item.id === targetId ? { ...item, ...updates } : item,
        ),
      );
    } catch (updateError) {
      setError(updateError.message);
    }
  };

  const handleUserDelete = async (targetId) => {
    if (!accessToken) return;
    const confirmed = window.confirm(t("adminPage.users.deleteConfirm"));
    if (!confirmed) return;
    try {
      await adminService.deleteUser(accessToken, targetId);
      setUsers((current) => current.filter((u) => u.id !== targetId));
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const handleStudentDelete = async (student) => {
    if (!accessToken || !student) return;
    const confirmed = window.confirm(
      t("adminPage.students.deleteConfirm", { name: student.full_name }),
    );
    if (!confirmed) return;
    try {
      await studentsService.deleteStudent(accessToken, student.id);
      const keep = (list) => (list || []).filter((s) => s.id !== student.id);
      setStudents(keep);
      setTimetableStudents(keep);
      setUsers((current) =>
        (current || []).filter(
          (u) =>
            !(u.kind === "child" && u.student_id === student.id) &&
            !(student.user_id && u.id === student.user_id),
        ),
      );
    } catch (deleteError) {
      setError(deleteError.message);
    }
  };

  const handlePaymentStatus = async (paymentId, status) => {
    if (!accessToken) return;
    try {
      await adminService.updatePayment(accessToken, {
        payment_id: paymentId,
        status,
      });
      setPayments((current) =>
        current.map((item) =>
          item.id === paymentId ? { ...item, status } : item,
        ),
      );
    } catch (paymentError) {
      setError(paymentError.message);
    }
  };

  const handleSupportRead = async (messageId) => {
    if (!accessToken) return;
    try {
      await adminService.markSupportRead(accessToken, messageId);
      setMessages((current) =>
        current.map((msg) =>
          msg.id === messageId ? { ...msg, is_read: true } : msg,
        ),
      );
    } catch (supportError) {
      setError(supportError.message);
    }
  };

  const openSupportReply = useCallback(
    (message) => {
      const to = getSupportSenderEmail(message);
      if (!to) return;

      const subjectBase = (message?.subject || "").trim();
      const subject = subjectBase
        ? `Re: ${subjectBase}`
        : t("adminPage.support.replySubjectFallback");

      setReplyDraft({
        messageId: message.id,
        to,
        subject,
        body: "",
      });
    },
    [getSupportSenderEmail, t],
  );

  const closeSupportReply = useCallback(() => {
    setReplyDraft(null);
    setSendingReply(false);
  }, []);

  const sendSupportReply = async () => {
    if (!accessToken || !replyDraft) return;
    if (!replyDraft.body || !replyDraft.body.trim()) {
      setError(t("adminPage.support.replyEmpty"));
      return;
    }

    setSendingReply(true);
    setError(null);
    try {
      await adminService.replySupport(accessToken, {
        message_id: replyDraft.messageId,
        subject: replyDraft.subject,
        body: replyDraft.body,
      });
      setMessages((current) =>
        current.map((msg) =>
          msg.id === replyDraft.messageId ? { ...msg, is_read: true } : msg,
        ),
      );
      closeSupportReply();
    } catch (replyError) {
      setError(replyError.message);
      setSendingReply(false);
    }
  };

  if (!isAdmin) {
    return (
      <div className="admin-dashboard" dir={dir}>
        <p>{t("adminPage.accessDenied")}</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard" dir={dir}>
      {error && (
        <div className="alert alert-error" role="alert">
          {error}
        </div>
      )}

      {loading ? (
        <p>{t("adminPage.loading")}</p>
      ) : (
        <div className="tab-content">
          {activeTab === "overview" && (
            <AdminOverview
              t={t}
              locale={locale}
              user={user}
              users={users}
              students={students}
              allStudents={timetableStudents}
              allTeachers={timetableTeachers}
              listCounts={listCounts}
              lessons={lessons}
              classGroups={classGroups}
              packs={packs}
              payments={payments}
              stats={stats}
              unreadCount={unreadCount}
              subjects={subjects}
              onNavigate={navigateToTab}
            />
          )}

          {activeTab === "teachers" && (
            <section>
              <div className="admin-section-head">
                <h2>{t("adminPage.teachers.title")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>

              <TeacherHoursPanel
                t={t}
                locale={locale}
                teachers={timetableTeachers}
                lessons={lessons}
              />

              <div className="toolbar">
                <label>
                  {t("adminPage.teachers.filter.label")}
                  <select
                    value={teachersStatusFilter}
                    onChange={(e) => {
                      setTeachersStatusFilter(e.target.value);
                      setTeachersPage(1);
                    }}
                  >
                    <option value="all">
                      {t("adminPage.teachers.filter.all")}
                    </option>
                    <option value="pending">
                      {t("adminPage.teachers.filter.pending")}
                    </option>
                    <option value="verified">
                      {t("adminPage.teachers.filter.verified")}
                    </option>
                  </select>
                </label>
              </div>

              {teachersError ? (
                <div className="alert alert-error">{teachersError}</div>
              ) : null}

              {teachersLoading ? (
                <p className="text-muted">{t("adminPage.loading")}</p>
              ) : teachers.length ? (
                <ul className="list">
                  {teachers.map((teacher) => {
                    const avatarUrl = getAvatarUrl(teacher);
                    const displayName =
                      teacher.full_name ||
                      `${teacher.first_name || ""} ${
                        teacher.last_name || ""
                      }`.trim() ||
                      teacher.email;

                    return (
                      <li key={teacher.id} className="list-card">
                        <div className="list-card__header">
                          <span className="admin-user-cell">
                            <span
                              className="admin-user-avatar"
                              aria-hidden="true"
                            >
                              {avatarUrl ? (
                                <img src={avatarUrl} alt="" loading="lazy" />
                              ) : (
                                getInitials(displayName)
                              )}
                            </span>
                            <span>{displayName}</span>
                          </span>

                          <span className="meta-chip">
                            {teacher.is_verified
                              ? t("adminPage.teachers.status.verified")
                              : t("adminPage.teachers.status.pending")}
                          </span>
                        </div>

                        <div className="list-card__meta">
                          <span>
                            {t("adminPage.teachers.fields.email")}:{" "}
                            {teacher.email}
                          </span>
                          <span>
                            {t("adminPage.teachers.fields.phone")}:{" "}
                            {teacher.phone_number || "-"}
                          </span>
                          <span>
                            {t("adminPage.teachers.fields.active")}:{" "}
                            {teacher.is_active
                              ? t("adminPage.teachers.activeYes")
                              : t("adminPage.teachers.activeNo")}
                          </span>
                          <span>
                            {t("adminPage.teachers.fields.joined")}:{" "}
                            {formatShortDate(teacher.date_joined, locale)}
                          </span>
                        </div>

                        <div
                          className="row-actions"
                          style={{ alignItems: "center" }}
                        >
                          <button
                            type="button"
                            className="btn ghost"
                            onClick={() =>
                              handleUserChange(teacher.id, {
                                is_verified: !teacher.is_verified,
                              })
                            }
                          >
                            {teacher.is_verified
                              ? t("adminPage.users.unverify")
                              : t("adminPage.users.verify")}
                          </button>

                          <label className="checkbox" style={{ margin: 0 }}>
                            <input
                              type="checkbox"
                              checked={Boolean(teacher.is_active)}
                              onChange={(e) =>
                                handleUserChange(teacher.id, {
                                  is_active: e.target.checked,
                                })
                              }
                            />
                            {t("adminPage.teachers.fields.active")}
                          </label>

                          {teacher.resume_url ? (
                            <a
                              className="btn ghost"
                              href={toAbsoluteUrl(teacher.resume_url)}
                              download
                              target="_blank"
                              rel="noreferrer"
                            >
                              <i
                                className="fa-solid fa-download"
                                aria-hidden="true"
                              ></i>{" "}
                              {t("adminPage.teachers.resume")}
                            </a>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="text-muted">{t("adminPage.teachers.empty")}</p>
              )}

              <Pagination
                page={teachersPage}
                totalPages={teachersTotalPages}
                dir={dir}
                disabled={teachersLoading}
                onPageChange={(nextPage) => setTeachersPage(nextPage)}
              />
            </section>
          )}

          {activeTab === "users" && (
            <section>
              <div className="admin-section-head">
                <h2>{t("adminPage.users.title")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>
              <div className="toolbar">
                <label>
                  {t("adminPage.users.filter")}
                  <select
                    value={userFilter}
                    onChange={(e) => {
                      setUserFilter(e.target.value);
                      setUsersPage(1);
                    }}
                  >
                    {userFilters.map((role) => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <div
                className="table"
                style={{
                  "--table-columns": "2fr 1.2fr 1.2fr 1fr 1fr 1.2fr 0.8fr 1fr",
                }}
              >
                <div className="table-head">
                  <span>{t("adminPage.users.columns.email")}</span>
                  <span>{t("adminPage.users.columns.name")}</span>
                  <span>{t("adminPage.users.columns.role")}</span>
                  <span>{t("adminPage.users.columns.phone")}</span>
                  <span>{t("adminPage.users.columns.joined")}</span>
                  <span>{t("adminPage.users.columns.verify")}</span>
                  <span>{t("adminPage.users.columns.active")}</span>
                  <span>{t("adminPage.users.columns.actions")}</span>
                </div>
                {filteredUsers.map((u) =>
                  u.kind === "child" ? (
                    // A child without their own login (managed by the parent).
                    <div className="table-row" key={u.id}>
                      <span className="admin-user-cell">
                        <span className="admin-user-avatar" aria-hidden="true">
                          {getInitials(u.full_name)}
                        </span>
                        <span className="text-muted">
                          {t("adminPage.users.noAccount")}
                          {u.parent_email
                            ? ` · ${t("adminPage.students.parent")}: ${u.parent_email}`
                            : ""}
                        </span>
                      </span>
                      <span>{u.full_name || "-"}</span>
                      <span>{t("adminPage.roles.student")}</span>
                      <span>-</span>
                      <span>
                        {u.date_joined
                          ? new Date(u.date_joined).toLocaleDateString()
                          : "-"}
                      </span>
                      <span>-</span>
                      <span>-</span>
                      <span className="row-actions">
                        <button
                          className="btn danger"
                          onClick={() =>
                            handleStudentDelete({
                              id: u.student_id,
                              full_name: u.full_name,
                            })
                          }
                        >
                          {t("adminPage.users.delete")}
                        </button>
                      </span>
                    </div>
                  ) : (
                    <div className="table-row" key={u.id}>
                      <span className="admin-user-cell">
                        <span className="admin-user-avatar" aria-hidden="true">
                          {getAvatarUrl(u) ? (
                            <img src={getAvatarUrl(u)} alt="" loading="lazy" />
                          ) : (
                            getInitials(u.full_name || u.email)
                          )}
                        </span>
                        <span>{u.email}</span>
                      </span>
                      <span>{u.full_name || "-"}</span>
                      <span>
                        {roleOptions.find((role) => role.value === u.role)
                          ?.label ||
                          u.role ||
                          "-"}
                      </span>
                      <span>{u.phone_number || "-"}</span>
                      <span>
                        {u.date_joined
                          ? new Date(u.date_joined).toLocaleDateString()
                          : "-"}
                      </span>
                      <span>
                        {u.role === "teacher" ? (
                          <button
                            className="btn ghost"
                            onClick={() =>
                              handleUserChange(u.id, {
                                is_verified: !u.is_verified,
                              })
                            }
                          >
                            {u.is_verified
                              ? t("adminPage.users.unverify")
                              : t("adminPage.users.verify")}
                          </button>
                        ) : (
                          "-"
                        )}
                      </span>
                      <span>
                        <input
                          type="checkbox"
                          checked={u.is_active}
                          onChange={(e) =>
                            handleUserChange(u.id, {
                              is_active: e.target.checked,
                            })
                          }
                          aria-label={t("adminPage.users.toggleActiveAria")}
                        />
                      </span>
                      <span className="row-actions">
                        <button
                          className="btn danger"
                          onClick={() => handleUserDelete(u.id)}
                        >
                          {t("adminPage.users.delete")}
                        </button>
                      </span>
                    </div>
                  ),
                )}
              </div>

              <Pagination
                page={usersPage}
                totalPages={usersTotalPages}
                dir={dir}
                disabled={loading}
                onPageChange={(nextPage) => loadUsersPage(nextPage, userFilter)}
              />
            </section>
          )}

          {activeTab === "students" && (
            <section>
              <div className="admin-section-head">
                <h2>{t("adminPage.students.title")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>
              <ul className="list">
                {students.map((s) => (
                  <li key={s.id} className="list-card">
                    <div className="list-card__header">
                      <span className="list-card__title">{s.full_name}</span>
                      {s.age ? (
                        <span className="meta-chip">
                          {s.age} {t("adminPage.students.ageYears")}
                        </span>
                      ) : null}
                    </div>
                    <div className="list-card__meta">
                      <span>
                        {t("adminPage.students.parent")}: {s.parent_email}
                      </span>
                      {s.date_of_birth ? (
                        <span>
                          {t("adminPage.students.dob")}:{" "}
                          {formatShortDate(s.date_of_birth, locale)}
                        </span>
                      ) : null}
                    </div>
                    {s.notes ? <p className="text-muted">{s.notes}</p> : null}
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn danger"
                        onClick={() => handleStudentDelete(s)}
                      >
                        <i className="fa-solid fa-trash" aria-hidden="true" />{" "}
                        {t("adminPage.users.delete")}
                      </button>
                    </div>
                  </li>
                ))}
              </ul>

              <Pagination
                page={studentsPage}
                totalPages={studentsTotalPages}
                dir={dir}
                disabled={loading}
                onPageChange={(nextPage) => loadStudentsPage(nextPage)}
              />
            </section>
          )}

          {activeTab === "lessons" && (
            <section>
              <div className="admin-section-head">
                <h2>{t("adminPage.lessons.title")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>

              <ul className="list">
                {lessonsForAdminList.map((lesson) => (
                  <li
                    key={lesson.__sessionKey || lesson.id}
                    className="list-card"
                  >
                    <div className="list-card__header">
                      <div>
                        <p className="list-card__title">{lesson.title}</p>
                        <p className="text-muted">
                          {t("adminPage.lessons.idLabel")} #{lesson.id}
                        </p>
                      </div>
                      <span className={`badge status-${lesson.status}`}>
                        {lessonStatusLabels[lesson.status] || lesson.status}
                      </span>
                    </div>

                    <div className="list-card__meta">
                      {lesson?.class_group?.id || lesson?.class_group_id ? (
                        <span>
                          {t("lessons.timetable.classLabel")}{" "}
                          {lesson?.class_group?.name || "-"}
                        </span>
                      ) : (
                        <span>
                          {t("adminPage.lessons.student")}:{" "}
                          {lesson.student.full_name}
                        </span>
                      )}
                      <span>
                        {t("adminPage.lessons.teacher")}:{" "}
                        {lesson.teacher.full_name || lesson.teacher.email}
                      </span>
                      <span>
                        {t("adminPage.lessons.duration")}:{" "}
                        {lesson.duration_minutes}{" "}
                        {t("adminPage.lessons.minutes")}
                      </span>
                    </div>

                    <div className="list-card__meta">
                      <span className="meta-chip">
                        {formatDate(lesson.scheduled_at, locale)}
                      </span>
                      {lesson.pack ? (
                        <span className="meta-chip">{lesson.pack.name}</span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              <Pagination
                page={lessonsPage}
                totalPages={lessonsTotalPages}
                dir={dir}
                disabled={loading}
                onPageChange={(nextPage) => loadLessonsPage(nextPage)}
              />
            </section>
          )}

          {activeTab === "timetable" && (
            <TimetableTab
              t={t}
              dir={dir}
              locale={locale}
              users={timetableTeachers}
              lessons={lessons}
              classGroups={classGroups}
              students={timetableStudents}
              packs={packs}
              accessToken={accessToken}
              onBack={goToOverview}
              onLessonsChange={setLessons}
              onError={setError}
            />
          )}

          {activeTab === "classes" && (
            <ClassesTab
              t={t}
              dir={dir}
              locale={locale}
              users={users}
              students={timetableStudents}
              classGroups={classGroups}
              accessToken={accessToken}
              onBack={goToOverview}
              onClassGroupsChange={setClassGroups}
              onError={setError}
            />
          )}

          {activeTab === "packs" && (
            <section>
              <div
                className="admin-section-head admin-section-head--full"
                style={{ marginBottom: "0.25rem" }}
              >
                <h2 style={{ margin: 0 }}>{t("adminPage.tabs.packs")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>

              <h3>{t("adminPage.packs.listTitle")}</h3>
              <ul className="list list--two-col">
                {packs.map((p) => (
                  <li
                    key={p.id}
                    className="list-card list-card--pack list-card--clickable"
                    style={{
                      "--pack-grad-start": p.gradient_start_color || "#E9F1FF",
                      "--pack-grad-end": p.gradient_end_color || "#FFFFFF",
                    }}
                    onClick={() => navigate(`/dashboard/packs/${p.id}`)}
                  >
                    <div className="list-card__header">
                      <div>
                        <span className="list-card__title">{p.name}</span>
                        {p.subject ? (
                          <p className="text-muted">
                            {typeof p.subject === "string"
                              ? p.subject
                              : p.subject.name}
                          </p>
                        ) : null}
                      </div>
                      <span className="meta-chip">
                        {formatCurrency(p.price_amount, "TND", locale)}
                      </span>
                    </div>

                    <div className="list-card__meta">
                      {p.lesson_count ? (
                        <span>
                          {p.lesson_count} {t("adminPage.packs.sessions")}
                        </span>
                      ) : null}
                      {p.session_length_minutes ? (
                        <span>
                          {p.session_length_minutes}{" "}
                          {t("adminPage.packs.minutesPerSession")}
                        </span>
                      ) : null}
                      {Array.isArray(p.resources) && p.resources.length ? (
                        <span>
                          {p.resources.length}{" "}
                          {t("adminPage.packs.resourcesCount")}
                        </span>
                      ) : null}
                      {p.is_featured ? (
                        <span className="meta-chip">
                          {t("adminPage.packs.badgeFeatured")}
                        </span>
                      ) : null}
                    </div>

                    {p.description || p.summary ? (
                      <p className="text-muted">
                        {(() => {
                          const raw = p.description || p.summary;
                          const normalized =
                            typeof raw === "string" ? raw.trim() : raw;
                          if (
                            typeof normalized === "string" &&
                            normalized.startsWith("offers.")
                          ) {
                            return t(normalized);
                          }
                          return normalized;
                        })()}
                      </p>
                    ) : null}
                  </li>
                ))}
              </ul>

              <Pagination
                page={packsPage}
                totalPages={packsTotalPages}
                dir={dir}
                disabled={loading}
                onPageChange={(nextPage) => loadPacksPage(nextPage)}
              />
            </section>
          )}

          {activeTab === "payments" && (
            <section>
              <div className="admin-section-head">
                <h2>{t("adminPage.payments.title")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>
              <div
                className="table"
                style={{ "--table-columns": "1.6fr 1.4fr 1fr 1fr" }}
              >
                <div className="table-head">
                  <span>{t("adminPage.payments.columns.user")}</span>
                  <span>{t("adminPage.payments.columns.pack")}</span>
                  <span>{t("adminPage.payments.columns.amount")}</span>
                  <span>{t("adminPage.payments.columns.status")}</span>
                </div>
                {payments.map((p) => (
                  <div key={p.id} className="table-row">
                    <span>{p.user?.email || p.user}</span>
                    <span>{p.pack?.name || p.pack}</span>
                    <span>{formatCurrency(p.amount, "TND", locale)}</span>
                    <span>
                      <select
                        value={p.status}
                        onChange={(e) =>
                          handlePaymentStatus(p.id, e.target.value)
                        }
                      >
                        {PAYMENT_STATUSES.map((status) => (
                          <option key={status} value={status}>
                            {paymentStatusLabels[status] || status}
                          </option>
                        ))}
                      </select>
                    </span>
                  </div>
                ))}
              </div>

              <Pagination
                page={paymentsPage}
                totalPages={paymentsTotalPages}
                dir={dir}
                disabled={loading}
                onPageChange={(nextPage) => loadPaymentsPage(nextPage)}
              />
            </section>
          )}

          {activeTab === "support" && (
            <section>
              <div className="admin-section-head">
                <h2>{t("adminPage.support.title")}</h2>
                <button
                  type="button"
                  className="admin-back"
                  onClick={goToOverview}
                >
                  <i
                    className={`fa-solid ${
                      dir === "rtl" ? "fa-arrow-right" : "fa-arrow-left"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span className="sr-only">
                    {t("adminPage.tabs.overview")}
                  </span>
                </button>
              </div>
              <ul className="list">
                {messages.map((m) => {
                  const senderEmail = getSupportSenderEmail(m);
                  return (
                    <li key={m.id} className="list-card">
                      <div className="list-card__header">
                        <div>
                          <span className="list-card__title">{m.subject}</span>
                          <p className="text-muted">
                            {t("adminPage.support.from")}:{" "}
                            {m.sender?.email || m.sender}
                          </p>
                        </div>
                        <span
                          className={`badge ${
                            m.is_read ? "status-completed" : "status-scheduled"
                          }`}
                        >
                          {m.is_read
                            ? t("adminPage.support.read")
                            : t("adminPage.support.new")}
                        </span>
                      </div>

                      {m.message ? <p>{m.message}</p> : null}

                      <div className="list-card__meta">
                        <span className="meta-chip">
                          {formatDate(m.created_at, locale)}
                        </span>
                        {m.type ? (
                          <span className="meta-chip">
                            {t("adminPage.support.type")}: {m.type}
                          </span>
                        ) : null}
                      </div>

                      {(senderEmail || !m.is_read) && (
                        <div className="list-card__actions">
                          {senderEmail ? (
                            <button
                              type="button"
                              className="btn ghost"
                              onClick={() => openSupportReply(m)}
                            >
                              {t("adminPage.support.reply")}
                            </button>
                          ) : null}
                          {!m.is_read ? (
                            <button
                              type="button"
                              className="btn ghost"
                              onClick={() => handleSupportRead(m.id)}
                            >
                              {t("adminPage.support.markRead")}
                            </button>
                          ) : null}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>

              <Pagination
                page={supportPage}
                totalPages={supportTotalPages}
                dir={dir}
                disabled={loading}
                onPageChange={(nextPage) => loadSupportPage(nextPage)}
              />

              {replyDraft && (
                <div
                  className="support-compose"
                  role="dialog"
                  aria-modal="true"
                >
                  <div className="support-compose__head">
                    <div className="support-compose__title">
                      {t("adminPage.support.replyTitle")}
                    </div>
                    <button
                      type="button"
                      className="support-compose__close"
                      onClick={closeSupportReply}
                      aria-label={t("adminPage.support.replyClose")}
                    >
                      <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                    </button>
                  </div>

                  <div className="support-compose__body">
                    <label className="support-compose__label">
                      <span>{t("adminPage.support.replyTo")}</span>
                      <input
                        className="support-compose__input"
                        value={replyDraft.to}
                        readOnly
                      />
                    </label>

                    <label className="support-compose__label">
                      <span>{t("adminPage.support.replySubject")}</span>
                      <input
                        className="support-compose__input"
                        value={replyDraft.subject}
                        onChange={(e) =>
                          setReplyDraft((current) =>
                            current
                              ? { ...current, subject: e.target.value }
                              : current,
                          )
                        }
                      />
                    </label>

                    <label className="support-compose__label">
                      <span>{t("adminPage.support.replyMessage")}</span>
                      <textarea
                        className="support-compose__textarea"
                        value={replyDraft.body}
                        onChange={(e) =>
                          setReplyDraft((current) =>
                            current
                              ? { ...current, body: e.target.value }
                              : current,
                          )
                        }
                      />
                    </label>
                  </div>

                  <div className="support-compose__actions">
                    <button
                      type="button"
                      className="btn"
                      onClick={sendSupportReply}
                      disabled={sendingReply}
                    >
                      {sendingReply
                        ? t("adminPage.support.replySending")
                        : t("adminPage.support.replySend")}
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}
    </div>
  );
}
